import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import GloablButton from "./GloablButton";
import "../styles/ProductCatalogDetails.css";
import PensilIcon from "../icons/BusinessHub/PensilIcon";
import ScannerIcon from "../icons/ScannerIcon";
import { userService } from "../services/userService";
import Utils from "../utils/Utils";
import ProductController from "../controllers/ProductController";
import SingleBarcodeScanner from "./SingleBarcodeScanner";
import CustomDropdown from "./StatusDropdown";
import ArrowDownIcon from "../icons/BusinessHub/ArrowDownIcon";
import RefreshIcon from "../icons/BusinessHub/RefreshIcon";
import { toast } from "react-toastify";
import CountriesController from "../controllers/CountriesController";
import colorName from "color-name";
// import UploadButtonIcon from "../icons/BusinessHub/Uploadbutton";
// import StarIcon from "../icons/StarIcon";
import PrimaryStar from "../icons/PrimaryStar";

// import XIcon from "../icons/XIcon";

// const AI_JOB_CACHE_KEY = "azalea-admin-ai-jobs";

const emptyProductDetails = {
  summary: {
    productName: "-",
    category: "-",
    status: "-",
  },
  basicInfo: {
    productName: "-",
    category: "-",
    categoryId: "",
    categoryName: "-",
    subCategoryId: "",
    subCategoryName: "-",
    brand: "-",
    model: "-",
    countryOfProduction: "-",
    description: "-",
    deliveryMethod: "-",
  },
  pricing: {
    defaultBasePrice: "",
    costPrice: "",
    profitMargin: "",
  },
  stock: {
    overallAvailability: "-",
    reserved: "-",
    damaged: "-",
  },
  history: {
    createdDate: "-",
    createdBy: "-",
    modifiedDate: "-",
    modifiedBy: "-",
  },
  technicalAttributes: [],
  technicalDescription: "-",
};

const emptyRawProductDetails = {
  basicInfo: {},
  changeHistory: {},
  pricingAndProfit: {},
  skus: [],
  stockInventory: {},
};

const DIMENSION_UNIT_OPTIONS = [
  { label: "cm", value: "CM" },
  { label: "m", value: "M" },
];

const WEIGHT_UNIT_OPTIONS = [
  { label: "Piece", value: "PIECE" },
  { label: "Kilogram", value: "KILOGRAM" },
  { label: "Pound", value: "POUND" },
  { label: "Set", value: "SET" },
];

const SIZE_OPTIONS = [
  { label: "Small", value: "SMALL" },
  { label: "Medium", value: "MEDIUM" },
  { label: "Large", value: "LARGE" },
  { label: "XL", value: "XL" },
  { label: "One Size", value: "ONE_SIZE" },
];

const UNIT_OF_MEASURE_OPTIONS = [
  { label: "Piece", value: "PIECE" },
  { label: "Kilogram", value: "KILOGRAM" },
  { label: "Pound", value: "POUND" },
  { label: "Set", value: "SET" },
];

const DELIVERY_METHOD_OPTIONS = [
  { label: "AZALEA", value: "AZALEA" },
  { label: "THIRD_PARTY", value: "THIRD_PARTY" },
];

const DESCRIPTION_MAX_LENGTH = 1000;

const normalizeStoredId = (value, ...keys) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    for (const key of keys) {
      const candidate = value?.[key];
      if (
        candidate !== null &&
        candidate !== undefined &&
        String(candidate).trim()
      ) {
        return String(candidate).trim();
      }
    }
  }
  return "";
};

const getApiErrorMessage = (err, fallback) => {
  const payload = err?.response?.data ?? err?.data;
  const status = err?.response?.status ?? err?.status;
  if (status === 404) {
    return (
      payload?.message ||
      payload?.error ||
      "Resource not found (404). Verify companyId/productId and try again."
    );
  }
  if (typeof payload === "string" && payload.trim()) return payload;
  if (Array.isArray(payload?.errors) && payload.errors.length) {
    return payload.errors.join(", ");
  }
  return payload?.message || payload?.error || err?.message || fallback;
};

const assertControllerSuccess = (response, fallbackMessage) => {
  const status = response?.status ?? response?.response?.status;
  if (status && status >= 200 && status < 300) return;

  const backendMessage =
    response?.data?.message ||
    response?.data?.error ||
    response?.response?.data?.message ||
    response?.response?.data?.error ||
    fallbackMessage;

  throw new Error(backendMessage);
};

const humanizeValue = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatTextValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";

  const rawValue = String(dateString).trim();
  const candidates = [
    rawValue,
    rawValue.replace(/\s+-\s+/, " "),
    rawValue.replace(/\//g, "-").replace(" ", "T"),
  ];

  const parsedDate = candidates
    .map((candidate) => new Date(candidate))
    .find((date) => !Number.isNaN(date.getTime()));

  if (!parsedDate) return rawValue;

  return `${parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })} - ${parsedDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;
};

const formatStockStatus = (quantity) => {
  const numericQuantity = Number(quantity);
  if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) {
    return "Out of Stock";
  }
  return "In Stock";
};

const formatQuantity = (quantity, unitOfMeasure) => {
  const quantityValue = formatTextValue(quantity);
  const unitValue = formatTextValue(unitOfMeasure);
  if (quantityValue === "-" && unitValue === "-") return "-";
  if (unitValue === "-") return quantityValue;
  if (quantityValue === "-") return unitValue;
  return `${quantityValue} ${unitValue.toLowerCase()}`;
};

const cleanDisplayValue = (value, fallback = "") => {
  if (value && typeof value === "object") return fallback ?? "";
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized === "-") return fallback;
  return normalized;
};

const sanitizeDecimalInput = (value) => {
  const cleaned = String(value || "").replace(/[^\d.]/g, "");
  const [integerPart, ...decimalParts] = cleaned.split(".");
  const decimalPart = decimalParts.join("");

  return decimalParts.length ? `${integerPart}.${decimalPart}` : integerPart;
};

const sanitizeIntegerInput = (value) => String(value || "").replace(/\D/g, "");

const getFirstNumberText = (value) => {
  if (value === null || value === undefined || typeof value === "object") {
    return "";
  }

  const match = String(value).match(/\d+(\.\d+)?/);
  return match?.[0] || "";
};

const getOptionValueSet = (options) =>
  new Set(options.map((option) => option.value));

// const normalizeCategoryValue = (value, fallback = "") => {
//   const cleaned = cleanDisplayValue(value, fallback);
//   if (!cleaned) return "";
//   return String(cleaned).trim().toUpperCase().replace(/\s+/g, "_");
// };

const findMatchingSubCategory = (subCategories, value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toUpperCase();

  if (!normalizedValue) return null;

  return (
    subCategories.find(
      (subCategory) =>
        String(subCategory?.subCategoryId || "")
          .trim()
          .toUpperCase() === normalizedValue ||
        String(subCategory?.subCategoryName || "")
          .trim()
          .toUpperCase() === normalizedValue,
    ) || null
  );
};

const pruneObject = (obj) =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    if (value === undefined) return acc;
    acc[key] = value;
    return acc;
  }, {});

const parsePayloadNumber = (value, fallback = null) => {
  const candidates = [value, fallback];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") {
      continue;
    }
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return candidate;
    }

    const extracted = String(candidate).match(/-?\d+(\.\d+)?/);
    if (!extracted) continue;

    const parsed = Number(extracted[0]);
    if (Number.isFinite(parsed)) return parsed;
  }

  return null;
};

const normalizeEnumValue = (value, fallback = "", defaultValue = "") => {
  const selected = cleanDisplayValue(value, cleanDisplayValue(fallback));
  if (!selected) return defaultValue;

  return String(selected)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

const normalizeDimensionUnit = (value, fallback = "CM") => {
  const normalized = normalizeEnumValue(value, fallback, "CM");
  const unitAliases = {
    CENTIMETER: "CM",
    CENTIMETERS: "CM",
    METER: "M",
    METERS: "M",
  };
  const selected = unitAliases[normalized] || normalized;

  return getOptionValueSet(DIMENSION_UNIT_OPTIONS).has(selected)
    ? selected
    : "CM";
};

const normalizeWeightUnit = (value, fallback = "KILOGRAM") => {
  const normalized = normalizeEnumValue(value, fallback, "KILOGRAM");
  const unitAliases = {
    KG: "KILOGRAM",
    KILO: "KILOGRAM",
    KILOGRAMS: "KILOGRAM",
    LB: "POUND",
    LBS: "POUND",
    POUNDS: "POUND",
    PIECES: "PIECE",
    SETS: "SET",
  };
  const selected = unitAliases[normalized] || normalized;

  return getOptionValueSet(WEIGHT_UNIT_OPTIONS).has(selected)
    ? selected
    : "KILOGRAM";
};

const normalizeUnitOfMeasure = (value, fallback = "PIECE") => {
  const normalized = normalizeEnumValue(value, fallback, "PIECE");

  return getOptionValueSet(UNIT_OF_MEASURE_OPTIONS).has(normalized)
    ? normalized
    : "PIECE";
};

const normalizeSize = (value, fallback = "ONE_SIZE") => {
  const normalized = normalizeEnumValue(value, fallback, "ONE_SIZE");

  return getOptionValueSet(SIZE_OPTIONS).has(normalized)
    ? normalized
    : "ONE_SIZE";
};

const extractUnitFromDisplay = (value) => {
  if (value === null || value === undefined || typeof value === "object") {
    return "";
  }

  const unitMatches = String(value).match(/[a-zA-Z]+/g);
  return unitMatches?.length ? unitMatches[unitMatches.length - 1] : "";
};

const buildDimensionPayload = (value, fallback = {}) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      height: parsePayloadNumber(value.height, fallback?.height),
      length: parsePayloadNumber(value.length, fallback?.length),
      unitOfDimensions: normalizeDimensionUnit(
        value.unitOfDimensions,
        fallback?.unitOfDimensions,
      ),
      width: parsePayloadNumber(value.width, fallback?.width),
    };
  }

  const displayValue = cleanDisplayValue(value);
  const numbers = displayValue.match(/-?\d+(\.\d+)?/g) || [];
  const unitFromDisplay = extractUnitFromDisplay(displayValue);

  return {
    height: parsePayloadNumber(numbers[2], fallback?.height),
    length: parsePayloadNumber(numbers[0], fallback?.length),
    unitOfDimensions: normalizeDimensionUnit(
      unitFromDisplay,
      fallback?.unitOfDimensions,
    ),
    width: parsePayloadNumber(numbers[1], fallback?.width),
  };
};

const buildWeightPayload = (displayValue, fallbackWeight, fallbackUnit) => {
  if (
    displayValue &&
    typeof displayValue === "object" &&
    !Array.isArray(displayValue)
  ) {
    return {
      weight: parsePayloadNumber(displayValue.weight, fallbackWeight),
      unit: normalizeWeightUnit(displayValue.unit, fallbackUnit),
    };
  }

  return {
    weight: parsePayloadNumber(displayValue, fallbackWeight),
    unit: normalizeWeightUnit(
      extractUnitFromDisplay(displayValue),
      fallbackUnit,
    ),
  };
};

const getDimensionDraftValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      length: sanitizeDecimalInput(value.length),
      width: sanitizeDecimalInput(value.width),
      height: sanitizeDecimalInput(value.height),
      unitOfDimensions: normalizeDimensionUnit(value.unitOfDimensions),
    };
  }

  const displayValue = cleanDisplayValue(value);
  const numbers = displayValue.match(/\d+(\.\d+)?/g) || [];

  return {
    length: numbers[0] || "",
    width: numbers[1] || "",
    height: numbers[2] || "",
    unitOfDimensions: normalizeDimensionUnit(
      extractUnitFromDisplay(displayValue),
    ),
  };
};

const getWeightDraftValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      weight: sanitizeDecimalInput(value.weight),
      unit: normalizeWeightUnit(value.unit),
    };
  }

  return {
    weight: getFirstNumberText(value),
    unit: normalizeWeightUnit(extractUnitFromDisplay(value)),
  };
};

const hasDimensionValue = (dimensionValue) =>
  ["length", "width", "height"].some((key) =>
    String(dimensionValue?.[key] ?? "").trim(),
  );

const hasValidNumber = (value) => {
  const normalized = sanitizeDecimalInput(value);
  if (!normalized) return false;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0;
};

const isCompleteDimensionValue = (value) => {
  const dimensionValue = getDimensionDraftValue(value);
  if (!hasDimensionValue(dimensionValue)) return true;

  return (
    hasValidNumber(dimensionValue.length) &&
    hasValidNumber(dimensionValue.width) &&
    hasValidNumber(dimensionValue.height)
  );
};

const isValidWeightValue = (value) => {
  const weightValue = getWeightDraftValue(value);
  if (!weightValue.weight) return true;

  return hasValidNumber(weightValue.weight);
};

const isValidOptionalNumber = (value) => {
  const rawValue = cleanDisplayValue(value);
  if (!rawValue) return true;

  const normalized = getFirstNumberText(value) || sanitizeDecimalInput(value);
  if (!normalized) return false;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0;
};

const validateDraftCatalog = ({
  draftProductDetails,
  draftSkuCards,
  selectedSubCategoryId,
  subCategories,
}) => {
  const errors = {};
  const addError = (key, message) => {
    if (!errors[key]) errors[key] = message;
  };
  const draftBasicInfo = draftProductDetails?.basicInfo || {};
  const draftPricing = draftProductDetails?.pricing || {};

  if (!cleanDisplayValue(draftBasicInfo.productName)) {
    addError("basicInfo.productName", "Product name is required.");
  }

  if (
    !cleanDisplayValue(draftBasicInfo.categoryId) &&
    !cleanDisplayValue(draftBasicInfo.categoryName, draftBasicInfo.category)
  ) {
    addError("basicInfo.category", "Select a category from the list.");
  }

  if (
    selectedSubCategoryId &&
    !findMatchingSubCategory(subCategories, selectedSubCategoryId)
  ) {
    addError("basicInfo.subCategory", "Select a valid sub category.");
  }

  if (!isValidOptionalNumber(draftPricing.costPrice)) {
    addError("pricing.costPrice", "Enter a valid non-negative price.");
  }

  draftSkuCards.forEach((draftSku, index) => {
    const prefix = `sku.${index}`;

    if (!isValidOptionalNumber(draftSku.quantity)) {
      addError(`${prefix}.quantity`, "Enter a valid non-negative quantity.");
    }

    if (!isValidOptionalNumber(draftSku.costPriceValue)) {
      addError(`${prefix}.costPriceValue`, "Enter a valid non-negative price.");
    }

    if (!isValidOptionalNumber(draftSku.price)) {
      addError(`${prefix}.price`, "Enter a valid non-negative price.");
    }

    if (!isCompleteDimensionValue(draftSku.productDimensions)) {
      addError(
        `${prefix}.productDimensions`,
        "Enter length, width, and height as non-negative numbers.",
      );
    }

    if (!isCompleteDimensionValue(draftSku.packageDimensions)) {
      addError(
        `${prefix}.packageDimensions`,
        "Enter length, width, and height as non-negative numbers.",
      );
    }

    if (!isValidWeightValue(draftSku.productWeight)) {
      addError(`${prefix}.productWeight`, "Enter a valid non-negative weight.");
    }

    if (!isValidWeightValue(draftSku.packageWeight)) {
      addError(`${prefix}.packageWeight`, "Enter a valid non-negative weight.");
    }

    if (draftSku.colorValue && !normalizeHexColor(draftSku.colorValue)) {
      addError(`${prefix}.color`, "Enter a valid hex color.");
    }
  });

  return errors;
};

const formatColorName = (name) =>
  String(name || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(
      /(blue|brown|coral|cyan|gold|goldenrod|gray|green|grey|khaki|lavender|magenta|orange|orchid|pink|purple|red|salmon|slate|turquoise|violet|white|yellow)/gi,
      " $1",
    )
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeColorSearchValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/grey/g, "gray")
    .replace(/[^a-z0-9]/g, "");

const rgbToHex = (rgb) =>
  `#${rgb
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;

const buildColorPreset = (rawName, rgb) => {
  const name = formatColorName(rawName);
  const value = rgbToHex(rgb);

  return {
    name,
    rawName,
    value,
    searchName: normalizeColorSearchValue(name),
    searchRawName: normalizeColorSearchValue(rawName),
    searchAliases: [
      normalizeColorSearchValue(name),
      normalizeColorSearchValue(rawName),
    ],
    searchHex: normalizeColorSearchValue(value),
  };
};

const COLOR_PRESETS = Array.from(
  Object.entries(colorName)
    .reduce((presetsByValue, [rawName, rgb]) => {
      const preset = buildColorPreset(rawName, rgb);
      const existingPreset = presetsByValue.get(preset.value);

      if (!existingPreset) {
        presetsByValue.set(preset.value, preset);
        return presetsByValue;
      }

      presetsByValue.set(preset.value, {
        ...existingPreset,
        searchAliases: Array.from(
          new Set([...existingPreset.searchAliases, ...preset.searchAliases]),
        ),
      });

      return presetsByValue;
    }, new Map())
    .values(),
);

const getColorSearchScore = (color, searchValue) => {
  const terms = [
    color.searchName,
    color.searchRawName,
    color.searchHex,
    ...color.searchAliases,
  ];
  const words = color.name
    .split(/\s+/)
    .map((word) => normalizeColorSearchValue(word));

  const getBestScore = (values, scoreForValue) =>
    values.reduce((bestScore, value) => {
      const score = scoreForValue(value);
      return score < bestScore ? score : bestScore;
    }, Number.POSITIVE_INFINITY);

  const fullNameScore = getBestScore(terms, (term) => {
    if (term === searchValue) return 0;
    if (term.startsWith(searchValue)) {
      return 100 + term.length - searchValue.length;
    }

    const index = term.indexOf(searchValue);
    if (index !== -1) {
      return 300 + index * 10 + term.length - searchValue.length;
    }

    return Number.POSITIVE_INFINITY;
  });

  const wordScore = getBestScore(words, (word) => {
    if (word === searchValue) return 200 + color.searchName.length;
    if (word.startsWith(searchValue)) {
      return 400 + color.searchName.length + word.length - searchValue.length;
    }

    const index = word.indexOf(searchValue);
    if (index !== -1) {
      return (
        500 +
        color.searchName.length +
        index * 10 +
        word.length -
        searchValue.length
      );
    }

    return Number.POSITIVE_INFINITY;
  });

  return Math.min(fullNameScore, wordScore);
};

const getFilteredColorPresets = (searchTerm) => {
  const searchValue = normalizeColorSearchValue(searchTerm);
  if (!searchValue) return COLOR_PRESETS;

  return COLOR_PRESETS.map((color, index) => ({
    color,
    index,
    score: getColorSearchScore(color, searchValue),
  }))
    .filter(({ score }) => Number.isFinite(score))
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map(({ color }) => color);
};

const normalizeHexColor = (value) => {
  const hex = String(value || "").trim();
  if (!hex) return null;

  const normalized = hex.startsWith("#") ? hex : `#${hex}`;
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalized)
    ? normalized.toUpperCase()
    : null;
};

const getColorNameFromHex = (hex) => {
  const matchedColor = COLOR_PRESETS.find(
    (color) => color.value.toLowerCase() === String(hex).toLowerCase(),
  );
  return matchedColor ? matchedColor.name : "Custom";
};

const normalizeSkuColor = (sku) => {
  const firstColor = Array.isArray(sku?.colorInfo) ? sku.colorInfo[0] : null;
  const rawColorName = String(firstColor?.colorName || "").trim();
  const rawColorValue = String(firstColor?.colorValue || "").trim();
  const colorValue = normalizeHexColor(rawColorValue) || "";
  const colorName =
    rawColorName || (colorValue ? getColorNameFromHex(colorValue) : "Custom");

  return {
    colorName,
    colorValue,
    color: rawColorName || formatTextValue(rawColorValue) || "-",
  };
};

const formatDimensions = (dimensions) => {
  if (!dimensions || typeof dimensions !== "object") return "-";

  const values = [
    dimensions.length,
    dimensions.width,
    dimensions.height,
  ].filter((value) => value !== null && value !== undefined && value !== "");

  if (!values.length) return "-";

  const unit = formatTextValue(dimensions.unitOfDimensions);
  return `${values.join(" x ")}${unit !== "-" ? ` ${unit.toLowerCase()}` : ""}`;
};

const formatWeight = (weight, unit) => {
  const weightValue = formatTextValue(weight);
  if (weightValue === "-") return "-";

  const unitValue = formatTextValue(unit);
  return `${weightValue}${unitValue !== "-" ? ` ${unitValue.toLowerCase()}` : ""}`;
};

const extractAttachmentUrl = (value) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";

  const markdownUrl = normalizedValue.match(/\[[^\]]+\]\((https?:\/\/[^)]+)\)/);
  const resolvedValue = markdownUrl?.[1] || normalizedValue;

  return resolvedValue.startsWith("http://") ||
    resolvedValue.startsWith("https://") ||
    resolvedValue.startsWith("data:image/")
    ? resolvedValue
    : "";
};

const buildAttachmentImageSource = (attachment) => {
  if (!attachment || typeof attachment !== "object") return "";

  const customPathUrl = extractAttachmentUrl(attachment.customPath);
  if (customPathUrl && customPathUrl !== "string") {
    return customPathUrl;
  }

  const rawData = String(attachment.data || "").trim();
  if (!rawData) return "";

  const dataUrl = extractAttachmentUrl(rawData);
  if (dataUrl) return dataUrl;

  const extension = String(attachment.extension || "").toLowerCase();
  if (
    !["png", "jpg", "jpeg", "gif", "webp", "svg", "avif"].includes(extension)
  ) {
    return "";
  }

  const mimeType =
    extension === "svg" ? "image/svg+xml" : `image/${extension || "png"}`;

  return `data:${mimeType};base64,${rawData}`;
};

const getAttachmentId = (attachment) =>
  String(attachment?.attachmentID ?? attachment?.attachmentId ?? "").trim();

const getSkuImageAttachments = (sku) => {
  const attachments = Array.isArray(sku?.productImages)
    ? sku.productImages
    : [];

  return attachments
    .map((attachment, index) => {
      const src = buildAttachmentImageSource(attachment);
      if (!src) return null;

      return {
        attachment,
        attachmentId: getAttachmentId(attachment),
        attachmentOrder: attachment?.attachmentOrder ?? index + 1,
        isPrimary: attachment?.isPrimary === true,
        src,
      };
    })
    .filter(Boolean);
};

const buildProductImagesFromSkuAttachments = (imageAttachments) =>
  imageAttachments.map((image, index) => ({
    ...image.attachment,
    attachmentOrder: index + 1,
  }));

const getSkuImages = (sku) => {
  return getSkuImageAttachments(sku).map((image) => image.src);
};

const normalizeSkuCards = (skus) =>
  skus.map((sku, index) => {
    const skuColor = normalizeSkuColor(sku);
    const stockQuantity = sku?.stockQuantity ?? "-";
    const unitOfMeasure = sku?.unitOfMeasure ?? "-";

    return {
      sku: formatTextValue(sku?.skuCode || sku?.skuID || `SKU ${index + 1}`),
      stock: formatStockStatus(stockQuantity),
      size: humanizeValue(sku?.size) || "-",
      material: formatTextValue(sku?.material),
      colorName: skuColor.colorName,
      colorValue: skuColor.colorValue,
      color: skuColor.color,
      quantity: formatQuantity(stockQuantity, unitOfMeasure),
      stockQuantityRaw: stockQuantity,
      unitOfMeasureRaw: formatTextValue(unitOfMeasure),
      unit: humanizeValue(unitOfMeasure) || "-",
      model: formatTextValue(sku?.model),
      barcode: formatTextValue(sku?.barcode),
      azaleaBarcode: formatTextValue(sku?.azaleaBarcode),
      productDimensions: formatDimensions(sku?.productDimensions),
      packageDimensions: formatDimensions(sku?.packageDimensions),
      imageAttachments: getSkuImageAttachments(sku),
      images: getSkuImages(sku),
      price: sku?.price,
      costPriceValue: sku?.costPriceValue,
      productWeight: formatWeight(
        sku?.productWeight,
        sku?.productWeightUnitOfMeasure,
      ),
      packageWeight: formatWeight(
        sku?.packageWeight,
        sku?.packageWeightUnitOfMeasure,
      ),
      skuId: formatTextValue(sku?.skuID || sku?.skuId),
      skuCode: formatTextValue(sku?.skuCode),
      specs: formatTextValue(sku?.technicalSpecs),
    };
  });

const normalizeProductDetails = (data) => {
  const skus = Array.isArray(data?.skus) ? data.skus : [];
  const firstSku = skus[0] || {};
  const firstSkuColor = normalizeSkuColor(firstSku);
  const reservedStockQuantities = skus
    .map((sku) => sku?.reservedStockQuantity)
    .filter(
      (quantity) =>
        quantity !== null && quantity !== undefined && quantity !== "",
    )
    .map(Number)
    .filter(Number.isFinite);
  const reservedStockQuantity = reservedStockQuantities.length
    ? reservedStockQuantities.reduce((total, quantity) => total + quantity, 0)
    : data?.stockInventory?.reserved;
  const basicInfo = data?.basicInfo || {};
  const pricingAndProfit = data?.pricingAndProfit || {};
  const stockInventory = data?.stockInventory || {};
  const changeHistory = data?.changeHistory || {};
  const productStatus =
    data?.productCompanyStatus || data?.productSystemStatus || "-";
  const categoryDisplayValue = basicInfo?.categoryName || basicInfo?.category;

  return {
    summary: {
      productName: formatTextValue(basicInfo?.productName),
      category: humanizeValue(categoryDisplayValue) || "-",
      status: humanizeValue(productStatus) || "-",
    },
    basicInfo: {
      productName: formatTextValue(basicInfo?.productName),
      category: humanizeValue(categoryDisplayValue) || "-",
      categoryId: formatTextValue(basicInfo?.categoryId),
      categoryName: formatTextValue(basicInfo?.categoryName),
      subCategoryId: formatTextValue(basicInfo?.subCategoryId),
      subCategoryName: formatTextValue(basicInfo?.subCategoryName),
      brand: formatTextValue(basicInfo?.brand),
      model: formatTextValue(basicInfo?.model),
      countryOfProduction: formatTextValue(basicInfo?.countryOfProduction),
      description: formatTextValue(basicInfo?.description),
      deliveryMethod: formatTextValue(basicInfo?.deliveryMethod),
    },
    pricing: {
      defaultBasePrice: formatTextValue(pricingAndProfit?.defaultBasePrice),
      costPrice: formatTextValue(pricingAndProfit?.costPrice),
      profitMargin: formatTextValue(pricingAndProfit?.profitMargin),
    },
    stock: {
      overallAvailability: formatTextValue(stockInventory?.overallAvailability),
      reserved: formatTextValue(reservedStockQuantity),
      damaged: formatTextValue(stockInventory?.damaged),
    },
    history: {
      createdDate: formatDateTime(changeHistory?.createdAt),
      createdBy: formatTextValue(changeHistory?.userName),
      modifiedDate: formatDateTime(changeHistory?.updatedAt),
      modifiedBy: formatTextValue(changeHistory?.userName),
    },
    technicalAttributes: [
      { label: "Material", value: formatTextValue(firstSku?.material) },
      {
        label: "Product Dimensions",
        value: formatDimensions(firstSku?.productDimensions),
      },
      {
        label: "Package Dimensions",
        value: formatDimensions(firstSku?.packageDimensions),
      },
      {
        label: "Product Weight",
        value: formatWeight(
          firstSku?.productWeight,
          firstSku?.productWeightUnitOfMeasure,
        ),
      },
      {
        label: "Package Weight",
        value: formatWeight(
          firstSku?.packageWeight,
          firstSku?.packageWeightUnitOfMeasure,
        ),
      },
      {
        label: "Unit of Measure",
        value: humanizeValue(firstSku?.unitOfMeasure) || "-",
      },
      {
        label: "Color",
        value: firstSkuColor.color,
      },
    ].filter((attribute) => attribute.value !== "-"),
    technicalDescription: formatTextValue(firstSku?.technicalSpecs),
  };
};

const getUserIdFromJwt = (parsedJwt, targetCompanyId) => {
  const scopedCompanyId =
    targetCompanyId || Object.keys(parsedJwt?.userDetails || {})[0];
  const companyScoped = parsedJwt?.userDetails?.[scopedCompanyId];

  return (
    parsedJwt?.userID ||
    parsedJwt?.userId ||
    parsedJwt?.id ||
    companyScoped?.employeeId ||
    companyScoped?.employeeID ||
    parsedJwt?.employeeID ||
    parsedJwt?.employeeId ||
    ""
  );
};

const generateRandom13Digits = () => {
  const digits = new Uint8Array(13);
  window.crypto.getRandomValues(digits);

  return Array.from(digits, (digit) => digit % 10).join("");
};

const generateCompanyBarcode = () => {
  const companyName = userService.getCompName() || "";

  const companyPrefix = companyName
    .replace(/[^a-zA-Z]/g, "")
    .substring(0, 2)
    .toUpperCase()
    .padEnd(2, "X");

  return `${companyPrefix}${generateRandom13Digits()}`;
};

function DetailField({
  label,
  value,
  isEditing,
  onChange,
  multiline = false,
  actionIcon = null,
  actionLabel = "",
  onActionClick,
  secondaryActionIcon = null,
  secondaryActionLabel = "",
  onSecondaryActionClick,
  inputMode,
  fieldType = "text",
  maxLength,
  error = "",
  colorValue = "",
  colorPickerRef = null,
  isColorPickerOpen = false,
  colorSearchValue = "",
  filteredColorPresets = [],
  onToggleColorPicker,
  onColorSearchChange,
  onColorSelect,
  onColorHexChange,
  onColorHexBlur,
  subCategories = [],
  selectedSubCategoryId = "",
  onSubCategoryChange,
}) {
  const [countries, setcountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const normalizeCategoryOption = (category) => ({
    categoryId: String(category?.categoryId || category?.id || "").trim(),
    categoryName: String(
      category?.categoryName || category?.label || "",
    ).trim(),
  });

  useEffect(() => {
    if (label !== "Country of Production") return;
    try {
      const getCountries = async () => {
        const countries = await CountriesController.getCountries({});
        setcountries(countries?.data?.map((item) => item.countryName));
      };
      getCountries();
    } catch (error) {
      console.log("error getting permisions");
    }
  }, [label]);
  useEffect(() => {
    if (label !== "Category") return;
    const getCategories = async () => {
      try {
        const response = await ProductController.getAllCategories({
          Authorization: userService.getUser(),
        });
        const categoryList = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.categories)
            ? response.data.categories
            : [];
        const normalizedCategories = categoryList
          .map(normalizeCategoryOption)
          .filter((category) => category.categoryId && category.categoryName);

        setCategories(normalizedCategories);
      } catch (error) {
        toast.error("Unable to load backend categories.");
      }
    };

    getCategories();
  }, [isEditing, label]);

  const inputClassName = `productCatalogDetails-edit-input ${
    error ? "is-invalid" : ""
  }`.trim();
  const textareaClassName = `productCatalogDetails-edit-textarea ${
    error ? "is-invalid" : ""
  }`.trim();
  const fieldError = error ? (
    <div className="productCatalogDetails-field-error">{error}</div>
  ) : null;

  const getTypedInputValue = () => {
    if (fieldType === "decimal") return sanitizeDecimalInput(value);
    if (fieldType === "integer") return sanitizeIntegerInput(value);
    return value;
  };

  const handleTypedInputChange = (event) => {
    const rawValue = event.target.value;

    if (fieldType === "decimal") {
      onChange(sanitizeDecimalInput(rawValue));
      return;
    }

    if (fieldType === "integer") {
      onChange(sanitizeIntegerInput(rawValue));
      return;
    }

    onChange(rawValue);
  };

  const handleMultilineChange = (event) => {
    const rawValue = event.target.value;
    onChange(maxLength ? rawValue.slice(0, maxLength) : rawValue);
  };

  const renderUnitDropdown = () => {
    const normalizedValue = normalizeUnitOfMeasure(value);

    return (
      <CustomDropdown
        rightIcon={<ArrowDownIcon />}
        value={normalizedValue}
        onChange={onChange}
        placeholder="Select Unit"
        options={UNIT_OF_MEASURE_OPTIONS}
        className="filter-popup-container-rows-input-container-cat"
        menuClassName="status-drop-menu-container-cat"
        optionClassName="status-drop-menu-option-countries-cat"
      />
    );
  };

  const renderDimensionEditor = () => {
    const dimensionValue = getDimensionDraftValue(value);
    const updateDimension = (fieldKey, nextValue) => {
      onChange({
        ...dimensionValue,
        [fieldKey]:
          fieldKey === "unitOfDimensions"
            ? normalizeDimensionUnit(nextValue)
            : sanitizeDecimalInput(nextValue),
      });
    };

    return (
      <div className="productCatalogDetails-measurement-editor">
        <input
          className={inputClassName}
          value={dimensionValue.length}
          inputMode="decimal"
          placeholder="Length"
          aria-label={`${label} length`}
          onChange={(event) => updateDimension("length", event.target.value)}
        />
        <input
          className={inputClassName}
          value={dimensionValue.width}
          inputMode="decimal"
          placeholder="Width"
          aria-label={`${label} width`}
          onChange={(event) => updateDimension("width", event.target.value)}
        />
        <input
          className={inputClassName}
          value={dimensionValue.height}
          inputMode="decimal"
          placeholder="Height"
          aria-label={`${label} height`}
          onChange={(event) => updateDimension("height", event.target.value)}
        />
        <CustomDropdown
          rightIcon={<ArrowDownIcon />}
          disabled
          value={dimensionValue.unitOfDimensions}
          onChange={(unit) => updateDimension("unitOfDimensions", unit)}
          placeholder="Unit"
          options={DIMENSION_UNIT_OPTIONS}
          className="filter-popup-container-rows-input-container-cat productCatalogDetails-measurement-unit"
          menuClassName="status-drop-menu-container-cat"
          optionClassName="status-drop-menu-option-countries-cat"
        />
      </div>
    );
  };

  const renderWeightEditor = () => {
    const weightValue = getWeightDraftValue(value);

    return (
      <div className="productCatalogDetails-measurement-editor productCatalogDetails-weight-editor">
        <input
          className={inputClassName}
          value={weightValue.weight}
          inputMode="decimal"
          placeholder="Weight"
          aria-label={`${label} weight`}
          onChange={(event) =>
            onChange({
              ...weightValue,
              weight: sanitizeDecimalInput(event.target.value),
            })
          }
        />
        <CustomDropdown
          rightIcon={<ArrowDownIcon />}
          disabled
          value={weightValue.unit}
          onChange={(unit) =>
            onChange({
              ...weightValue,
              unit: normalizeWeightUnit(unit),
            })
          }
          placeholder="Unit"
          options={WEIGHT_UNIT_OPTIONS}
          className="filter-popup-container-rows-input-container-cat productCatalogDetails-measurement-unit"
          menuClassName="status-drop-menu-container-cat"
          optionClassName="status-drop-menu-option-countries-cat"
        />
      </div>
    );
  };

  return (
    <div className="productCatalogDetails-basic-field">
      <div className="productCatalogDetails-field-label">{label}</div>
      {isEditing ? (
        multiline ? (
          <>
            <textarea
              className={textareaClassName}
              value={value}
              maxLength={maxLength}
              onChange={handleMultilineChange}
              aria-invalid={Boolean(error)}
            />
            {maxLength ? (
              <div className="productCatalogDetails-char-counter">
                {String(value || "").length}/{maxLength}
              </div>
            ) : null}
          </>
        ) : fieldType === "unitOfMeasure" ? (
          renderUnitDropdown()
        ) : fieldType === "dimension" ? (
          renderDimensionEditor()
        ) : fieldType === "weight" ? (
          renderWeightEditor()
        ) : actionIcon ? (
          <div className="productCatalogDetails-edit-input-action-wrap">
            <input
              className={`${inputClassName} productCatalogDetails-edit-input-with-action`}
              value={getTypedInputValue()}
              inputMode={inputMode}
              onChange={handleTypedInputChange}
              aria-invalid={Boolean(error)}
            />
            <button
              type="button"
              className="productCatalogDetails-edit-input-action"
              onClick={onActionClick}
              aria-label={actionLabel}
            >
              {actionIcon}
            </button>
            {secondaryActionIcon ? (
              <button
                type="button"
                className="productCatalogDetails-edit-input-action"
                onClick={onSecondaryActionClick}
                aria-label={secondaryActionLabel}
              >
                {secondaryActionIcon}
              </button>
            ) : null}
          </div>
        ) : label === "Category" ? (
          (() => {
            const categoryDisplayValue =
              value && typeof value === "object"
                ? value.categoryName || value.categoryId
                : value;
            const selectedCategory = categories.find(
              (category) =>
                category.categoryId === categoryDisplayValue ||
                category.categoryName === categoryDisplayValue,
            );

            return (
              <CustomDropdown
                searchable
                rightIcon={<ArrowDownIcon />}
                placeholder="Select Category"
                value={selectedCategory?.categoryId || ""}
                onChange={(categoryId) => {
                  const category = categories.find(
                    (item) => item.categoryId === categoryId,
                  );

                  onChange(category || categoryId);
                }}
                options={categories.map((category) => ({
                  label: category.categoryName,
                  value: category.categoryId,
                }))}
                className="filter-popup-container-rows-input-container-cat"
                menuClassName="status-drop-menu-container-cat"
                optionClassName="status-drop-menu-option-countries-cat"
              />
            );
          })()
        ) : label === "Country of Production" ? (
          <CustomDropdown
            rightIcon={<ArrowDownIcon />}
            searchable={true}
            value={cleanDisplayValue(value)}
            onChange={onChange}
            placeholder="Select country"
            options={countries}
            className="filter-popup-container-rows-input-container-cat"
            menuClassName="status-drop-menu-container-cat"
            optionClassName="status-drop-menu-option-countries"
          />
        ) : label === "Delivery Method" ? (
          <CustomDropdown
            rightIcon={<ArrowDownIcon />}
            value={cleanDisplayValue(value)}
            onChange={onChange}
            placeholder="Select Delivery Method"
            options={DELIVERY_METHOD_OPTIONS}
            className="filter-popup-container-rows-input-container-cat"
            menuClassName="status-drop-menu-container-cat"
            optionClassName="status-drop-menu-option-countries"
          />
        ) : label === "Size" ? (
          <CustomDropdown
            rightIcon={<ArrowDownIcon />}
            value={normalizeSize(value)}
            onChange={onChange}
            placeholder="Select Size"
            options={SIZE_OPTIONS}
            className="filter-popup-container-rows-input-container-cat"
            menuClassName="status-drop-menu-container-cat"
            optionClassName="status-drop-menu-option-countries"
          />
        ) : label === "Sub Category" ? (
          <CustomDropdown
            searchable
            rightIcon={<ArrowDownIcon />}
            value={selectedSubCategoryId}
            onChange={onSubCategoryChange}
            placeholder="Select Sub Category"
            options={subCategories.map((subCategory) => ({
              label: subCategory.subCategoryName,
              value: subCategory.subCategoryId,
            }))}
            className="filter-popup-container-rows-input-container-cat"
            menuClassName="status-drop-menu-container-cat"
            optionClassName="status-drop-menu-option-countries-cat"
          />
        ) : label === "Color" ? (
          <div
            className="productCatalogDetails-color-selector-wrap"
            ref={colorPickerRef}
          >
            <div className="productCatalogDetails-color-hex-trigger">
              <div
                className="color-swatch-button"
                onClick={onToggleColorPicker}
                style={{
                  width: "20px",
                  height: "20px",
                  background: normalizeHexColor(colorValue) || "#000000",
                }}
              />
              <input
                className={`${inputClassName} productCatalogDetails-color-input`}
                value={colorValue}
                placeholder="#000000"
                maxLength={7}
                onChange={(event) => onColorHexChange?.(event.target.value)}
                onBlur={onColorHexBlur}
                aria-invalid={Boolean(error)}
              />
            </div>
            {isColorPickerOpen ? (
              <div className="color-selector-menu">
                <input
                  className="color-preset-search-input"
                  value={colorSearchValue}
                  placeholder="Search color"
                  onChange={(event) =>
                    onColorSearchChange?.(event.target.value)
                  }
                />
                {filteredColorPresets.length ? (
                  <div className="color-presets-grid">
                    {filteredColorPresets.map((color) => (
                      <div
                        key={color.value}
                        className="color-preset-button"
                        onClick={() => onColorSelect?.(color.name, color.value)}
                      >
                        <span
                          className="color-preset-dot"
                          style={{ background: color.value }}
                        />
                        <span className="color-preset-label">{color.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="color-preset-empty">No colors found</div>
                )}
                <div className="color-picker-background">
                  <input
                    type="color"
                    value={normalizeHexColor(colorValue) || "#000000"}
                    onChange={(event) =>
                      onColorSelect?.(
                        getColorNameFromHex(event.target.value),
                        event.target.value.toUpperCase(),
                      )
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <input
            className={inputClassName}
            value={getTypedInputValue()}
            inputMode={inputMode}
            onChange={handleTypedInputChange}
            aria-invalid={Boolean(error)}
          />
        )
      ) : (
        <div className="productCatalogDetails-field-value">{value}</div>
      )}
      {fieldError}
    </div>
  );
}

function ProductCatalogDetails() {
  const productImageInputRef = useRef(null);
  const imageUploadTargetRef = useRef(null);
  const [imageAdding, setImageAdding] = useState(false);
  // const [imageEnhancing, setImageEnhancing] = useState(false);
  // const [imageDeleting, setImageDeleting] = useState(false);
  // const [openImagePopup, setOpenImagePopup] = useState(false);
  // const [activeImages, setActiveImages] = useState([]);
  const [activeProduct, setActiveProduct] = useState(null);
  // const [replaceOriginal, setReplaceOriginal] = useState(false);
  // const [currentIndex, setCurrentIndex] = useState(0);
  // const [actionError, setActionError] = useState("");
  // const [aiJobs, setAiJobs] = useState({});

  const location = useLocation();
  const jwt = userService.getUser();
  const parsedJwt = (() => {
    try {
      return jwt ? Utils.parseJwt(jwt) : null;
    } catch (error) {
      return null;
    }
  })();
  //****************************************************************************************************** */
  let navigationData = location.state;

  if (!navigationData) {
    const params = new URLSearchParams(location.search);
    const encryptedData = params.get("data");

    // console.log("URL:", location.search);
    // console.log("encryptedData:", encryptedData);

    if (encryptedData) {
      try {
        const decryptedData = Utils.AESDecryptNavigation(encryptedData);

        // console.log("decryptedData:", decryptedData);
        // console.log("decryptedData type:", typeof decryptedData);

        navigationData =
          typeof decryptedData === "string"
            ? JSON.parse(decryptedData)
            : decryptedData;

        // console.log("navigationData:", navigationData);
        // console.log("isEditing:", navigationData.isEditing);
      } catch (error) {
        console.error("Failed to decrypt/parse navigation data:", error);
        navigationData = {};
      }
    }
  }

  navigationData = navigationData || {};
  //************************************************************************************************************* */
  const companyId = userService.getCompID();
  const businessProfileID = userService.getBPID();
  const productId =
    normalizeStoredId(navigationData?.productId, "productId", "productID") ||
    normalizeStoredId(navigationData?.productID);

  const [isEditing, setIsEditing] = useState(navigationData.isEditing);
  const [productDetails, setProductDetails] = useState(emptyProductDetails);
  const [skuCards, setSkuCards] = useState([]);
  const [catalogDetailsLoading, setCatalogDetailsLoading] = useState(false);
  const [catalogDetailsError, setCatalogDetailsError] = useState("");
  const [catalogSaveLoading, setCatalogSaveLoading] = useState(false);
  const [catalogSaveMessage, setCatalogSaveMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [draftProductDetails, setDraftProductDetails] =
    useState(emptyProductDetails);
  const [draftSkuCards, setDraftSkuCards] = useState([]);
  const [rawProductDetails, setRawProductDetails] = useState(
    emptyRawProductDetails,
  );
  const [barcodeScannerTarget, setBarcodeScannerTarget] = useState(null);
  const [openColorPickerSkuId, setOpenColorPickerSkuId] = useState(null);
  const [colorPresetSearchBySkuId, setColorPresetSearchBySkuId] = useState({});
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");
  const [draggedSkuImage, setDraggedSkuImage] = useState(null);
  const [dragOverSkuImage, setDragOverSkuImage] = useState(null);
  const [imageOrderSavingSkuIndex, setImageOrderSavingSkuIndex] =
    useState(null);
  const [primaryAttachmentSavingId, setPrimaryAttachmentSavingId] =
    useState(null);
  const [dirtySkuImageOrderIndexes, setDirtySkuImageOrderIndexes] = useState(
    {},
  );
  const colorDropdownRefs = useRef({});

  const loadSubCategories = useCallback(async (categoryId) => {
    const cleanedCategoryId = cleanDisplayValue(categoryId);

    if (!cleanedCategoryId) {
      setSubCategories([]);
      return;
    }

    try {
      const response = await ProductController.getSubCategoriesByCategory({
        Authorization: userService.getUser(),
        request: { categoryId: cleanedCategoryId },
      });

      setSubCategories(
        Array.isArray(response?.data)
          ? response.data.filter((item) => item.displayToUsers === true)
          : [],
      );
    } catch (error) {
      setSubCategories([]);
      toast.error("Unable to load backend sub categories.");
    }
  }, []);

  /************************************************************************************************* */
  // const [videoDragging, setVideoDragging] = useState(false);
  // const videoInputRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [displayedProd, setDisplayedProd] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(productId);

  const AddVideoToProduct = async () => {
    const productRequestDto = {
      basicInfo: {
        brand: displayedProd?.basicInfo?.brand,
        category: displayedProd?.basicInfo?.category,
        categoryId: displayedProd?.basicInfo?.categoryId,
        countryOfProduction: displayedProd?.basicInfo?.countryOfProduction,
        description: displayedProd?.basicInfo?.description,
        model: displayedProd?.basicInfo?.model,
        productName: displayedProd?.basicInfo?.productName,
      },
      companyId: displayedProd?.basicInfo?.categoryId,
      skus: [
        {
          barcode: displayedProd?.skus?.[0]?.barcode,
          colorInfo: displayedProd?.skus?.[0]?.colorInfo,
          costPriceValue: displayedProd?.skus?.[0]?.costPriceValue,
          currency: displayedProd?.skus?.[0]?.currency,
          dimensions: {
            height: displayedProd?.skus?.[0]?.dimensions?.height,
            length: displayedProd?.skus?.[0]?.dimensions?.length,
            unitOfDimensions:
              displayedProd?.skus?.[0]?.dimensions?.unitOfDimensions,
            width: displayedProd?.skus?.[0]?.dimensions?.width,
          },
          initialStockQuantity: displayedProd?.skus?.[0]?.initialStockQuantity,
          material: displayedProd?.skus?.[0]?.material,
          price: displayedProd?.skus?.[0]?.price,
          productImages: displayedProd?.skus?.[0]?.productImages,
          productVideos: [
            ...(displayedProd?.skus?.[0]?.productVideos || []),
            {
              attachmentStatus: "DEFAULT",
              attachmentType: "PRODUCT_VIDEO",
              countryName: displayedProd?.basicInfo?.countryOfProduction,
              customPath: "string",
              data: (await Utils.processVideoFile(videoFile)).base64,
              extension: videoFile.name.split(".").pop().toLowerCase(),
              objectName: videoFile.name,
              userName: Utils.parseJwt(userService.getUser()).username,
            },
          ],
          reservedStockQuantity:
            displayedProd?.skus?.[0]?.reservedStockQuantity,
          size: displayedProd?.skus?.[0]?.size,
          skuCode: displayedProd?.skus?.[0]?.skuCode,
          skuID: displayedProd?.skus?.[0]?.skuID,
          stockQuantity: displayedProd?.skus?.[0]?.stockQuantity,
          technicalSpecs: displayedProd?.skus?.[0]?.technicalSpecs,
          unitOfMeasure: displayedProd?.skus?.[0]?.unitOfMeasure,
        },
      ],
      userId: "377899800-e89b-12d3-a456-426614174000",
      videoUrls: [videoUrl],
    };
    try {
      const updateProduct = await ProductController.updateProduct({
        Authorization: userService.getUser(),
        productId: selectedProduct,
        productRequestDto: productRequestDto,
      });
      console.log("1");

      if (!updateProduct) {
        console.log("sdcsadc");
      }
    } catch (error) {
      console.log(error);
    }
  };

  /******************************************************************************* */
  useEffect(() => {
    const fetchProductCatalogDetails = async () => {
      if (!jwt) {
        setCatalogDetailsError("Missing authentication token.");
        setProductDetails(emptyProductDetails);
        setSkuCards([]);
        return;
      }

      if (!businessProfileID || !companyId || !productId) {
        setCatalogDetailsError(
          "Missing businessProfileID, companyId, or productId.",
        );
        setProductDetails(emptyProductDetails);
        setSkuCards([]);
        return;
      }
      const productRequest = {
        businessProfileID: businessProfileID,
        companyId: companyId,
        productId: productId,
      };
      try {
        setCatalogDetailsLoading(true);
        setCatalogDetailsError("");
        setCatalogSaveMessage("");
        const response = await ProductController.getProductCatalogDetails({
          productRequest: productRequest,
          Authorization: userService.getUser(),
        });
        assertControllerSuccess(
          response,
          "Failed to load product catalog details.",
        );
        setSelectedProduct(productId);
        const data = response?.data ?? response?.response?.data;
        if (!data || typeof data !== "object") {
          throw new Error("Failed to load product catalog details.");
        }

        const normalizedProductDetails = normalizeProductDetails(data);
        const normalizedSkuCards = normalizeSkuCards(
          Array.isArray(data?.skus) ? data.skus : [],
        );

        setProductDetails(normalizedProductDetails);
        setDraftProductDetails(normalizedProductDetails);
        setSkuCards(normalizedSkuCards);
        setDraftSkuCards(normalizedSkuCards);
        setRawProductDetails(data);
        setFieldErrors({});
        setDirtySkuImageOrderIndexes({});
      } catch (error) {
        setCatalogDetailsError(
          getApiErrorMessage(error, "Failed to load product catalog details."),
        );
        setProductDetails(emptyProductDetails);
        setDraftProductDetails(emptyProductDetails);
        setSkuCards([]);
        setDraftSkuCards([]);
        setRawProductDetails(emptyRawProductDetails);
        setSubCategories([]);
        setSelectedSubCategoryId("");
        setFieldErrors({});
        setDirtySkuImageOrderIndexes({});
      } finally {
        setCatalogDetailsLoading(false);
      }
    };

    fetchProductCatalogDetails();
  }, [businessProfileID, companyId, jwt, productId]);

  useEffect(() => {
    const rawBasicInfo = rawProductDetails?.basicInfo || {};
    const categoryId = cleanDisplayValue(rawBasicInfo?.categoryId);
    const subCategoryId = cleanDisplayValue(rawBasicInfo?.subCategoryId);

    setSelectedSubCategoryId(subCategoryId);
    loadSubCategories(categoryId);
  }, [loadSubCategories, rawProductDetails?.basicInfo]);

  const applyCatalogDetailsSnapshot = useCallback((data) => {
    const normalizedProductDetails = normalizeProductDetails(data);
    const normalizedSkuCards = normalizeSkuCards(
      Array.isArray(data?.skus) ? data.skus : [],
    );

    setProductDetails(normalizedProductDetails);
    setDraftProductDetails(normalizedProductDetails);
    setSkuCards(normalizedSkuCards);
    setDraftSkuCards(normalizedSkuCards);
    setRawProductDetails(data);
    setFieldErrors({});
    setDirtySkuImageOrderIndexes({});
    return { normalizedProductDetails, normalizedSkuCards };
  }, []);

  const refreshProductCatalogDetails = useCallback(
    async ({ silent = true } = {}) => {
      if (!jwt || !businessProfileID || !companyId || !productId) {
        throw new Error("Missing businessProfileID, companyId, or productId.");
      }

      const productRequest = {
        businessProfileID,
        companyId,
        productId,
      };

      try {
        if (!silent) {
          setCatalogDetailsLoading(true);
        }
        const response = await ProductController.getProductCatalogDetails({
          productRequest,
          Authorization: userService.getUser(),
        });
        assertControllerSuccess(
          response,
          "Failed to load product catalog details.",
        );

        const data = response?.data ?? response?.response?.data;
        if (!data || typeof data !== "object") {
          throw new Error("Failed to load product catalog details.");
        }

        applyCatalogDetailsSnapshot(data);
        return data;
      } finally {
        if (!silent) {
          setCatalogDetailsLoading(false);
        }
      }
    },
    [applyCatalogDetailsSnapshot, businessProfileID, companyId, jwt, productId],
  );

  // const getMediaUrl = (media) => {
  //   if (typeof media === "string") return media;
  //   if (!media || typeof media !== "object") return "";

  //   return (
  //     buildAttachmentImageSource(media) ||
  //     media?.url ||
  //     media?.imageUrl ||
  //     media?.imageURL ||
  //     media?.path ||
  //     media?.s3Url ||
  //     media?.s3URL ||
  //     ""
  //   );
  // };

  // const getMediaKind = (media) => {
  //   const explicitType = String(media?.attachmentType || media?.type || "")
  //     .trim()
  //     .toUpperCase();
  //   if (explicitType.includes("VIDEO")) return "video";

  //   const extension = String(media?.extension || "")
  //     .trim()
  //     .toLowerCase();
  //   if (["mp4", "webm", "mov", "m4v", "avi"].includes(extension)) {
  //     return "video";
  //   }

  //   const url = getMediaUrl(media).toLowerCase();
  //   if (/\.(mp4|webm|mov|m4v|avi)(\?|$)/.test(url)) return "video";
  //   return "image";
  // };

  // const getCurrentImage = () => activeImages?.[currentIndex] || null;

  // const getCurrentImageAttachmentId = () => {
  //   const image = getCurrentImage();
  //   return (
  //     image?.attachmentID ||
  //     image?.attachmentId ||
  //     image?.attachment_id ||
  //     image?.id ||
  //     null
  //   );
  // };

  // const getSkuProductImages = (sku) =>
  //   Array.isArray(sku?.productImages)
  //     ? sku.productImages.filter((image) => getMediaUrl(image))
  //     : [];

  // const getCatalogSkuByIndex = (skuIndex) =>
  //   Array.isArray(rawProductDetails?.skus)
  //     ? rawProductDetails.skus[skuIndex]
  //     : null;

  const buildSkuImageTarget = (
    skuCard,
    skuIndex,
    details = rawProductDetails,
  ) => {
    const sku = Array.isArray(details?.skus) ? details.skus[skuIndex] : null;
    const resolvedSkuId =
      cleanDisplayValue(skuCard?.skuId) ||
      cleanDisplayValue(sku?.skuID) ||
      cleanDisplayValue(sku?.skuId);
    const resolvedProductId =
      cleanDisplayValue(details?.productId) ||
      cleanDisplayValue(details?.productID) ||
      productId;

    return {
      productId: resolvedProductId,
      id: resolvedProductId,
      skuId: resolvedSkuId,
      skuIndex,
      name: `${productDetails?.summary?.productName || "Product"} - ${
        skuCard?.sku || `SKU ${skuIndex + 1}`
      }`.trim(),
      details,
    };
  };

  const setActiveSkuImageTarget = (skuCard, skuIndex, openPopup = false) => {
    const target = buildSkuImageTarget(skuCard, skuIndex);
    // const sku = getCatalogSkuByIndex(skuIndex);
    // const images = getSkuProductImages(sku);

    imageUploadTargetRef.current = target;
    setActiveProduct(target);
    // setActiveImages(images);
    // setReplaceOriginal(false);
    // setActionError("");
    // setCurrentIndex(0);
    // if (openPopup) {
    //   setOpenImagePopup(true);
    // }
  };

  // const openAiEnhanceForSku = (skuCard, skuIndex) => {
  //   setActiveSkuImageTarget(skuCard, skuIndex, true);
  // };

  const openImageUploadForSku = (skuCard, skuIndex) => {
    setActiveSkuImageTarget(skuCard, skuIndex, false);
    window.setTimeout(() => productImageInputRef.current?.click(), 0);
  };

  // const getUrlExtension = (url) => {
  //   const cleanUrl = String(url || "")
  //     .split("?")[0]
  //     .split("#")[0];
  //   const extension = cleanUrl.split(".").pop();
  //   return String(extension || "").toLowerCase();
  // };

  // const getImageMimeType = (image) => {
  //   const extension =
  //     String(image?.extension || "").toLowerCase() ||
  //     getUrlExtension(getMediaUrl(image));
  //   if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  //   if (extension === "png") return "image/png";
  //   if (extension === "webp") return "image/webp";
  //   if (extension === "gif") return "image/gif";
  //   return "string";
  // };

  // const getBase64Parts = (dataUrl) => {
  //   const match = String(dataUrl || "").match(/^data:(.*?);base64,(.*)$/);
  //   if (match) {
  //     return { mimeType: match[1] || "string", base64: match[2] || "" };
  //   }
  //   return { mimeType: "string", base64: dataUrl || "" };
  // };

  // const readAiJobCache = () => {
  //   try {
  //     const parsed = JSON.parse(localStorage.getItem(AI_JOB_CACHE_KEY) || "{}");
  //     return parsed && typeof parsed === "object" ? parsed : {};
  //   } catch (error) {
  //     return {};
  //   }
  // };

  // const writeAiJobCache = (nextValue) => {
  //   try {
  //     localStorage.setItem(AI_JOB_CACHE_KEY, JSON.stringify(nextValue));
  //   } catch (error) {}
  // };

  // const mergeAiJobs = useCallback((incomingJobs) => {
  //   if (!Array.isArray(incomingJobs) || !incomingJobs.length) return;

  //   setAiJobs((prev) => {
  //     const next = { ...(prev || {}) };
  //     incomingJobs.forEach((job) => {
  //       const jobProductId = String(job?.productId || "");
  //       const jobId = String(job?.jobId || "");
  //       if (!jobProductId || !jobId) return;
  //       const existing = Array.isArray(next[jobProductId])
  //         ? next[jobProductId]
  //         : [];
  //       const filtered = existing.filter(
  //         (entry) => String(entry?.jobId || "") !== jobId,
  //       );
  //       next[jobProductId] = [job, ...filtered].slice(0, 30);
  //     });
  //     writeAiJobCache(next);
  //     return next;
  //   });
  // }, []);

  // const loadProductAiJobs = useCallback(
  //   async (targetProductId) => {
  //     if (!targetProductId) return [];
  //     try {
  //       const response = await ProductController.getProductAiJobs({
  //         Authorization: userService.getUser(),
  //         productId: targetProductId,
  //       });
  //       assertControllerSuccess(response, "Failed to load AI jobs.");
  //       const jobs = response?.data ?? response?.response?.data ?? [];
  //       mergeAiJobs(jobs);
  //       return jobs;
  //     } catch (error) {
  //       return [];
  //     }
  //   },
  //   [mergeAiJobs],
  // );

  // useEffect(() => {
  //   setAiJobs(readAiJobCache());
  // }, []);

  // useEffect(() => {
  //   setCurrentIndex(0);
  // }, [activeImages]);

  // useEffect(() => {
  //   const targetProductId = activeProduct?.productId || activeProduct?.id;
  //   if (openImagePopup && targetProductId) {
  //     loadProductAiJobs(targetProductId);
  //   }
  // }, [
  //   activeProduct?.id,
  //   activeProduct?.productId,
  //   loadProductAiJobs,
  //   openImagePopup,
  // ]);

  // const getJobsForProduct = useCallback(
  //   (targetProductId) => {
  //     const key = String(targetProductId || "");
  //     return Array.isArray(aiJobs?.[key]) ? aiJobs[key] : [];
  //   },
  //   [aiJobs],
  // );

  // const isPendingAiJob = (job) =>
  //   !job?.done && ["QUEUED", "IN_PROGRESS"].includes(String(job?.status || ""));

  // const formatAiJobTypeLabel = (jobType) =>
  //   String(jobType || "")
  //     .toLowerCase()
  //     .split("_")
  //     .filter(Boolean)
  //     .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  //     .join(" ");

  // const activePendingAiJobs = React.useMemo(() => {
  //   const targetProductId = activeProduct?.productId || activeProduct?.id;
  //   return getJobsForProduct(targetProductId).filter((job) =>
  //     isPendingAiJob(job),
  //   );
  // }, [activeProduct?.id, activeProduct?.productId, getJobsForProduct]);
  // const activePendingAiJob = activePendingAiJobs[0] || null;
  // const activeAiJobMessage =
  //   activePendingAiJob?.message || "Enhancing your image...";

  // const queueAiJob = async ({ start, request, successMessage }) => {
  //   const response = await start({
  //     Authorization: userService.getUser(),
  //     request,
  //   });
  //   assertControllerSuccess(response, "Failed to queue AI job.");
  //   const job = response?.data ?? response?.response?.data;
  //   if (!job?.jobId) {
  //     throw new Error("The AI job did not return a job ID.");
  //   }
  //   mergeAiJobs([job]);
  //   if (successMessage) {
  //     setActionError(successMessage);
  //   }
  //   return job;
  // };

  // const buildEnhanceImageRequest = async (image) => {
  //   const details = activeProduct?.details || rawProductDetails || {};
  //   const imageUrl = getMediaUrl(image);
  //   const dataUrl = await Utils.imageUrlToBase64(imageUrl);
  //   const base64Parts = getBase64Parts(dataUrl);

  //   if (!base64Parts?.base64 || base64Parts.base64 === "string") {
  //     throw new Error("Failed to prepare image for AI enhancement.");
  //   }

  //   const mimeType =
  //     base64Parts.mimeType !== "string" &&
  //     base64Parts.mimeType !== "application/octet-stream"
  //       ? base64Parts.mimeType
  //       : getImageMimeType(image);

  //   return {
  //     createdById: Utils.getUserIdFromJwt(jwt),
  //     existingAttachmentId: getCurrentImageAttachmentId(),
  //     imageBase64: base64Parts.base64,
  //     imageMimeType: mimeType,
  //     images: [
  //       {
  //         imageBase64: base64Parts.base64,
  //         label: image?.objectName || image?.label || "product-image",
  //         mimeType,
  //       },
  //     ],
  //     objectName:
  //       image?.objectName ||
  //       details?.basicInfo?.productName ||
  //       activeProduct?.name ||
  //       "product-image",
  //     productId:
  //       details?.productId ||
  //       details?.productID ||
  //       activeProduct?.productId ||
  //       activeProduct?.id ||
  //       null,
  //     productName: details?.basicInfo?.productName || activeProduct?.name || "",
  //     prompt:
  //       "Improve the product image for ecommerce use with cleaner lighting, sharper edges, accurate colors, and better clarity while preserving the real product shape and materials.",
  //     quality: "high",
  //     replaceOriginal,
  //     size: "1024x1024",
  //     skuId: activeProduct?.skuId || null,
  //     userName: parsedJwt?.username || parsedJwt?.sub || "system",
  //   };
  // };

  // const handleEnhanceActiveImage = async () => {
  //   const currentImage = getCurrentImage();
  //   if (!currentImage) return;
  //   if (getMediaKind(currentImage) !== "image") {
  //     setActionError(
  //       "Image enhancement can only be applied to image attachments.",
  //     );
  //     return;
  //   }
  //   try {
  //     setImageEnhancing(true);
  //     setActionError("");
  //     const request = await buildEnhanceImageRequest(currentImage);
  //     await queueAiJob({
  //       start: ProductController.startEnhanceProductImageJob,
  //       request,
  //       successMessage: "AI image enhancement queued.",
  //     });
  //     toast.success("AI image enhancement queued.");
  //   } catch (error) {
  //     setActionError(getApiErrorMessage(error, "AI image enhancement failed."));
  //   } finally {
  //     setImageEnhancing(false);
  //   }
  // };

  // const handleEnhanceAllActiveImages = async () => {
  //   const imageAttachments = activeImages.filter(
  //     (image) => getMediaKind(image) === "image",
  //   );
  //   if (!imageAttachments.length) return;

  //   try {
  //     setImageEnhancing(true);
  //     setActionError("");
  //     for (const image of imageAttachments) {
  //       const request = await buildEnhanceImageRequest(image);
  //       await queueAiJob({
  //         start: ProductController.startEnhanceProductImageJob,
  //         request,
  //       });
  //     }
  //     setActionError("AI image enhancements queued.");
  //     toast.success("AI image enhancements queued.");
  //   } catch (error) {
  //     setActionError(getApiErrorMessage(error, "AI image enhancement failed."));
  //   } finally {
  //     setImageEnhancing(false);
  //   }
  // };

  // const handleDeleteActiveImage = async () => {
  //   const attachmentId = getCurrentImageAttachmentId();
  //   if (!attachmentId) {
  //     setActionError(
  //       "The selected image cannot be deleted because no attachment ID was found.",
  //     );
  //     return;
  //   }

  //   try {
  //     setImageDeleting(true);
  //     setActionError("");
  //     const response = await ProductController.deleteProductAttachment({
  //       Authorization: userService.getUser(),
  //       request: {
  //         attachmentId,
  //         productId: activeProduct?.productId || activeProduct?.id || productId,
  //         skuId: activeProduct?.skuId || null,
  //       },
  //     });
  //     assertControllerSuccess(response, "Failed to delete product image.");
  //     const refreshed = await refreshProductCatalogDetails();
  //     const refreshedSku = Array.isArray(refreshed?.skus)
  //       ? refreshed.skus[activeProduct?.skuIndex]
  //       : null;
  //     setActiveProduct((prev) => ({
  //       ...prev,
  //       details: refreshed,
  //     }));
  //     setActiveImages(getSkuProductImages(refreshedSku));
  //     toast.success("Product image deleted.");
  //   } catch (error) {
  //     setActionError(
  //       getApiErrorMessage(error, "Failed to delete product image."),
  //     );
  //   } finally {
  //     setImageDeleting(false);
  //   }
  // };

  const startEditing = () => {
    setDraftProductDetails(productDetails);
    setDraftSkuCards(skuCards);
    setFieldErrors({});
    setCatalogSaveMessage("");
    setDirtySkuImageOrderIndexes({});
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraftProductDetails(productDetails);
    setDraftSkuCards(skuCards);
    setFieldErrors({});
    setCatalogSaveMessage("");
    setDirtySkuImageOrderIndexes({});
    setSelectedSubCategoryId(
      cleanDisplayValue(rawProductDetails?.basicInfo?.subCategoryId),
    );
    loadSubCategories(rawProductDetails?.basicInfo?.categoryId);
    setIsEditing(false);
  };
  const saveEditing = async () => {
    if (!jwt || !productId || !companyId) {
      setCatalogSaveMessage("Missing jwt, companyId, or productId.");
      return;
    }

    const nextFieldErrors = validateDraftCatalog({
      draftProductDetails,
      draftSkuCards,
      selectedSubCategoryId,
      subCategories,
    });

    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      setCatalogSaveMessage("Fix the highlighted fields before saving.");
      return;
    }

    setFieldErrors({});

    const userId = String(getUserIdFromJwt(parsedJwt, companyId) || "").trim();
    const rawBasicInfo = rawProductDetails?.basicInfo || {};
    const draftBasicInfo = draftProductDetails?.basicInfo || {};
    const draftCategory = draftBasicInfo?.category;
    const selectedCategory =
      draftCategory && typeof draftCategory === "object" ? draftCategory : {};
    const categoryDisplayValue =
      selectedCategory?.categoryName || draftCategory;
    const nextCategoryName =
      selectedCategory?.categoryName ||
      cleanDisplayValue(rawBasicInfo?.categoryName, categoryDisplayValue);
    const rawCategoryId = cleanDisplayValue(rawBasicInfo?.categoryId);
    const nextCategoryId = cleanDisplayValue(
      selectedCategory?.categoryId || draftBasicInfo?.categoryId,
      rawCategoryId,
    );
    const categoryWasEdited =
      Boolean(selectedCategory?.categoryId) &&
      cleanDisplayValue(selectedCategory.categoryId) !== rawCategoryId;
    const selectedSubCategory = findMatchingSubCategory(
      subCategories,
      selectedSubCategoryId ||
        draftBasicInfo?.subCategoryId ||
        draftBasicInfo?.subCategoryName,
    );
    const draftSubCategoryId = cleanDisplayValue(
      selectedSubCategory?.subCategoryId || draftBasicInfo?.subCategoryId,
    );
    const draftSubCategoryName = cleanDisplayValue(
      selectedSubCategory?.subCategoryName || draftBasicInfo?.subCategoryName,
    );
    const categoryChangedWithoutSubCategory =
      categoryWasEdited && !draftSubCategoryId && !draftSubCategoryName;
    const nextSubCategoryId = categoryChangedWithoutSubCategory
      ? null
      : cleanDisplayValue(draftSubCategoryId, rawBasicInfo?.subCategoryId);
    const nextSubCategoryName = categoryChangedWithoutSubCategory
      ? null
      : cleanDisplayValue(draftSubCategoryName, rawBasicInfo?.subCategoryName);

    const nextSkus = (rawProductDetails?.skus || []).map((sku, index) => {
      const draftSku = draftSkuCards[index] || {};
      const rawColorInfo = Array.isArray(sku?.colorInfo) ? sku.colorInfo : [];
      const primaryColor = rawColorInfo[0];
      const productDimensions = buildDimensionPayload(
        draftSku.productDimensions,
        sku?.productDimensions || sku?.dimensions || {},
      );
      const packageDimensions = buildDimensionPayload(
        draftSku.packageDimensions,
        sku?.packageDimensions || {},
      );
      const dimensions = buildDimensionPayload(
        draftSku.productDimensions,
        sku?.dimensions || productDimensions,
      );
      const productWeight = buildWeightPayload(
        draftSku.productWeight,
        sku?.productWeight,
        sku?.productWeightUnitOfMeasure,
      );
      const packageWeight = buildWeightPayload(
        draftSku.packageWeight,
        sku?.packageWeight,
        sku?.packageWeightUnitOfMeasure,
      );
      const nextColorName = cleanDisplayValue(
        draftSku.colorName,
        primaryColor?.colorName || draftSku.color,
      );
      const nextColorValue = cleanDisplayValue(
        draftSku.colorValue,
        primaryColor?.colorValue,
      );
      const normalizedNextColorValue = normalizeHexColor(nextColorValue);

      return pruneObject({
        azaleaBarcode:
          draftSku.azaleaBarcode !== undefined
            ? cleanDisplayValue(draftSku.azaleaBarcode, sku?.azaleaBarcode)
            : cleanDisplayValue(sku?.azaleaBarcode),
        barcode:
          draftSku.barcode !== undefined && draftSku.barcode !== "-"
            ? cleanDisplayValue(draftSku.barcode)
            : cleanDisplayValue(sku?.barcode),
        colorInfo: rawColorInfo.length
          ? rawColorInfo.map((color, colorIndex) =>
              colorIndex === 0
                ? pruneObject({
                    ...color,
                    colorName: nextColorName || color?.colorName || "Custom",
                    colorValue:
                      normalizedNextColorValue ||
                      color?.colorValue ||
                      undefined,
                  })
                : color,
            )
          : nextColorName || normalizedNextColorValue
            ? [
                pruneObject({
                  colorName: nextColorName || "Custom",
                  colorValue: normalizedNextColorValue || undefined,
                }),
              ]
            : [],
        costPriceValue: parsePayloadNumber(
          draftSku.costPriceValue,
          sku?.costPriceValue ??
            draftProductDetails?.pricing?.costPrice ??
            rawProductDetails?.pricingAndProfit?.costPrice,
        ),
        currency: cleanDisplayValue(sku?.currency, "USD"),
        dimensions,
        initialStockQuantity: parsePayloadNumber(
          sku?.initialStockQuantity,
          sku?.stockQuantity,
        ),
        material:
          draftSku.material !== undefined
            ? cleanDisplayValue(draftSku.material, sku?.material)
            : cleanDisplayValue(sku?.material),
        model:
          draftSku.model !== undefined
            ? cleanDisplayValue(draftSku.model, sku?.model)
            : cleanDisplayValue(sku?.model),
        packageDimensions,
        packageWeight: packageWeight.weight,
        packageWeightUnitOfMeasure: packageWeight.unit,
        price: parsePayloadNumber(
          draftSku.price,
          sku?.price ?? rawProductDetails?.pricingAndProfit?.defaultBasePrice,
        ),
        productDimensions,
        // productImages: Array.isArray(sku?.productImages)
        //   ? sku.productImages
        //   : [],
        // productVideos: Array.isArray(sku?.productVideos)
        //   ? sku.productVideos
        //   : [],
        productWeight: productWeight.weight,
        productWeightUnitOfMeasure: productWeight.unit,
        reservedStockQuantity: parsePayloadNumber(
          sku?.reservedStockQuantity,
          0,
        ),
        size:
          draftSku.size !== undefined && cleanDisplayValue(draftSku.size)
            ? normalizeSize(draftSku.size, sku?.size)
            : normalizeSize(sku?.size),
        skuID:
          draftSku.skuId !== undefined
            ? cleanDisplayValue(draftSku.skuId, sku?.skuID)
            : sku?.skuID,
        skuCode:
          draftSku.skuCode !== undefined
            ? cleanDisplayValue(draftSku.skuCode, sku?.skuCode)
            : sku?.skuCode,
        stockQuantity:
          draftSku.quantity !== undefined && draftSku.quantity !== "-"
            ? parsePayloadNumber(draftSku.quantity, sku?.stockQuantity)
            : parsePayloadNumber(sku?.stockQuantity),
        unitOfMeasure:
          draftSku.unit !== undefined && draftSku.unit !== "-"
            ? normalizeUnitOfMeasure(draftSku.unit, sku?.unitOfMeasure)
            : normalizeUnitOfMeasure(sku?.unitOfMeasure),
        technicalSpecs:
          draftSku.specs !== undefined
            ? cleanDisplayValue(draftSku.specs, sku?.technicalSpecs)
            : sku?.technicalSpecs,
      });
    });

    const updatePayload = {
      companyId,
      basicInfo: {
        brand: cleanDisplayValue(draftBasicInfo.brand, rawBasicInfo?.brand),
        categoryId: nextCategoryId,
        categoryName: cleanDisplayValue(nextCategoryName),
        countryOfProduction: cleanDisplayValue(
          draftBasicInfo.countryOfProduction,
          rawBasicInfo?.countryOfProduction || "",
        ),
        description: cleanDisplayValue(
          draftBasicInfo.description,
          rawBasicInfo?.description,
        ),
        productName: cleanDisplayValue(
          draftBasicInfo.productName,
          rawBasicInfo?.productName,
        ),
        subCategoryId: nextSubCategoryId,
        subCategoryName: nextSubCategoryName,
        deliveryMethod: draftBasicInfo.deliveryMethod,
      },
      pricingAndProfit: {
        defaultBasePrice: cleanDisplayValue(
          draftProductDetails.pricing.defaultBasePrice,
          rawProductDetails?.pricingAndProfit?.defaultBasePrice || "",
        ),
        costPrice: cleanDisplayValue(
          draftProductDetails.pricing.costPrice,
          rawProductDetails?.pricingAndProfit?.costPrice || "",
        ),
        profitMargin: cleanDisplayValue(
          draftProductDetails.pricing.profitMargin,
          rawProductDetails?.pricingAndProfit?.profitMargin || "",
        ),
      },
      changeHistory: {
        createdAt: formatDateTime(rawProductDetails?.changeHistory?.createdAt),
        updatedAt: formatDateTime(
          cleanDisplayValue(
            draftProductDetails.history.modifiedDate,
            rawProductDetails?.changeHistory?.updatedAt,
          ),
        ),
        userName:
          Utils.parseJwt(userService.getUser()).username ??
          cleanDisplayValue(
            draftProductDetails.history.modifiedBy,
            rawProductDetails?.changeHistory?.userName,
          ),
      },
      productCompanyStatus:
        String(rawProductDetails?.productCompanyStatus || "")
          .trim()
          .toUpperCase() || "APPROVED",
      productSystemStatus:
        String(rawProductDetails?.productSystemStatus || "")
          .trim()
          .toUpperCase() || "APPROVED",
      skus: nextSkus,
      stockInventory: {
        overallAvailability: cleanDisplayValue(
          draftProductDetails.stock.overallAvailability,
          rawProductDetails?.stockInventory?.overallAvailability,
        ),
        reserved: parsePayloadNumber(
          draftProductDetails.stock.reserved,
          rawProductDetails?.stockInventory?.reserved || 0,
        ),
        damaged: parsePayloadNumber(
          draftProductDetails.stock.damaged,
          rawProductDetails?.stockInventory?.damaged || 0,
        ),
      },
      userId: Utils.parseJwt(userService.getUser()).id,
    };

    if (userId) {
      updatePayload.userId = userId;
    }

    try {
      setCatalogSaveLoading(true);
      setCatalogSaveMessage("");

      const response = await ProductController.updateProductCatalog({
        productId,
        request: updatePayload,
        Authorization: userService.getUser(),
      });
      console.log("updatePayload", updatePayload);
      assertControllerSuccess(response, "Failed to update product catalog.");

      const nextRawProductDetails = {
        ...rawProductDetails,
        ...updatePayload,
      };
      const nextProductDetails = normalizeProductDetails(nextRawProductDetails);
      const nextSkuCards = normalizeSkuCards(updatePayload.skus);

      setProductDetails(nextProductDetails);
      setDraftProductDetails(nextProductDetails);
      setSkuCards(nextSkuCards);
      setDraftSkuCards(nextSkuCards);
      setRawProductDetails(nextRawProductDetails);
      setCatalogSaveMessage("Product updated successfully.");
      setIsEditing(false);
      setTimeout(() => {
        window.location.reload();
      }, 700);
    } catch (error) {
      setCatalogSaveMessage(
        getApiErrorMessage(error, "Failed to update product catalog."),
      );
    } finally {
      setCatalogSaveLoading(false);
    }
  };

  const clearFieldError = (fieldKey) => {
    setFieldErrors((prev) => {
      if (!prev[fieldKey]) return prev;

      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const setDraftSectionValue = (sectionKey, fieldKey, value) => {
    clearFieldError(`${sectionKey}.${fieldKey}`);
    setDraftProductDetails((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [fieldKey]: value,
      },
    }));
  };

  const handleCategoryChange = (value) => {
    const selectedCategory = value && typeof value === "object" ? value : null;
    const categoryId = cleanDisplayValue(selectedCategory?.categoryId);

    clearFieldError("basicInfo.category");
    clearFieldError("basicInfo.subCategory");
    setDraftProductDetails((prev) => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        category: value,
        categoryId,
        categoryName: cleanDisplayValue(selectedCategory?.categoryName),
        subCategoryId: "",
        subCategoryName: "",
      },
    }));

    setSelectedSubCategoryId("");
    loadSubCategories(categoryId);
  };

  const selectSubCategory = (subCategoryId) => {
    const matchedSubCategory = findMatchingSubCategory(
      subCategories,
      subCategoryId,
    );

    clearFieldError("basicInfo.subCategory");
    setSelectedSubCategoryId(subCategoryId);
    setDraftProductDetails((prev) => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        subCategoryId: matchedSubCategory?.subCategoryId || subCategoryId || "",
        subCategoryName: matchedSubCategory?.subCategoryName || "",
      },
    }));
  };

  useEffect(() => {
    if (!subCategories.length || selectedSubCategoryId) return;

    const draftBasicInfo = draftProductDetails?.basicInfo || {};
    const matchedSubCategory = findMatchingSubCategory(
      subCategories,
      draftBasicInfo.subCategoryId || draftBasicInfo.subCategoryName,
    );

    if (!matchedSubCategory) return;

    setSelectedSubCategoryId(matchedSubCategory.subCategoryId);
    setDraftProductDetails((prev) => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        subCategoryId: matchedSubCategory.subCategoryId,
        subCategoryName: matchedSubCategory.subCategoryName,
      },
    }));
  }, [draftProductDetails?.basicInfo, selectedSubCategoryId, subCategories]);

  const setDraftSkuValue = (skuIndex, fieldKey, value) => {
    clearFieldError(`sku.${skuIndex}.${fieldKey}`);
    setDraftSkuCards((prev) =>
      prev.map((skuCard, index) =>
        index === skuIndex ? { ...skuCard, [fieldKey]: value } : skuCard,
      ),
    );
  };

  const handleGenerateAzaleaBarcode = (skuIndex) => {
    setDraftSkuValue(skuIndex, "azaleaBarcode", generateCompanyBarcode());
  };

  const handleGenerateBarcode = (skuIndex) => {
    setDraftSkuValue(skuIndex, "barcode", generateRandom13Digits());
  };

  const getSkuColorPickerId = (skuCard, skuIndex) =>
    String(skuCard?.skuId || skuCard?.skuCode || `sku-${skuIndex}`);

  const setDraftSkuColor = (skuIndex, colorNameValue, colorHexValue) => {
    const normalizedColorValue = colorHexValue
      ? String(colorHexValue).toUpperCase()
      : "";

    clearFieldError(`sku.${skuIndex}.color`);
    setDraftSkuCards((prev) =>
      prev.map((skuCard, index) =>
        index === skuIndex
          ? {
              ...skuCard,
              color: colorNameValue || normalizedColorValue,
              colorName: colorNameValue || "Custom",
              colorValue: normalizedColorValue,
            }
          : skuCard,
      ),
    );
  };

  const updateColorPresetSearch = (skuId, value) => {
    setColorPresetSearchBySkuId((prev) => ({
      ...prev,
      [skuId]: value,
    }));
  };

  const handleColorHexChange = (skuIndex, value) => {
    const trimmed = String(value || "").replace(/\s/g, "");

    if (trimmed === "") {
      setDraftSkuColor(skuIndex, "Custom", "");
      return;
    }

    if (!/^#?[0-9a-fA-F]{0,6}$/.test(trimmed)) return;

    const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    const upper = withHash.toUpperCase();
    const normalized = normalizeHexColor(upper);

    setDraftSkuColor(
      skuIndex,
      normalized ? getColorNameFromHex(normalized) : "Custom",
      upper,
    );
  };

  const handleColorHexBlur = (skuIndex) => {
    const draftSku = draftSkuCards[skuIndex] || {};
    const normalized = normalizeHexColor(draftSku.colorValue);

    if (!normalized) return;

    setDraftSkuColor(skuIndex, getColorNameFromHex(normalized), normalized);
  };

  const applySkuImageOrder = (skuIndex, imageAttachments) => {
    const orderedImageAttachments = imageAttachments.map((image, index) => ({
      ...image,
      attachmentOrder: index + 1,
      attachment: {
        ...image.attachment,
        attachmentOrder: index + 1,
      },
    }));
    const imageSources = orderedImageAttachments.map((image) => image.src);
    const updateCards = (cards) =>
      cards.map((skuCard, index) =>
        index === skuIndex
          ? {
              ...skuCard,
              imageAttachments: orderedImageAttachments,
              images: imageSources,
            }
          : skuCard,
      );

    setSkuCards(updateCards);
    setDraftSkuCards(updateCards);
    setRawProductDetails((prev) => ({
      ...prev,
      skus: Array.isArray(prev?.skus)
        ? prev.skus.map((sku, index) =>
            index === skuIndex
              ? {
                  ...sku,
                  productImages: buildProductImagesFromSkuAttachments(
                    orderedImageAttachments,
                  ),
                }
              : sku,
          )
        : [],
    }));
  };

  const handleSkuImageDragStart = (event, skuIndex, attachmentId) => {
    if (!attachmentId || imageOrderSavingSkuIndex !== null) {
      event.preventDefault();
      return;
    }

    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "text/plain",
      JSON.stringify({ skuIndex, attachmentId }),
    );
    setDraggedSkuImage({ skuIndex, attachmentId });
  };

  const handleSkuImageDragOver = (event, skuIndex, attachmentId) => {
    if (
      !attachmentId ||
      !draggedSkuImage ||
      draggedSkuImage.skuIndex !== skuIndex ||
      draggedSkuImage.attachmentId === attachmentId
    ) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverSkuImage({ skuIndex, attachmentId });
  };

  const clearSkuImageDragState = () => {
    setDraggedSkuImage(null);
    setDragOverSkuImage(null);
  };

  const handleSkuImageDrop = (event, skuIndex, targetAttachmentId) => {
    event.preventDefault();

    const sourceImage = draggedSkuImage;
    clearSkuImageDragState();

    if (
      !sourceImage ||
      sourceImage.skuIndex !== skuIndex ||
      sourceImage.attachmentId === targetAttachmentId
    ) {
      return;
    }

    const skuCard = currentSkuCards[skuIndex];
    const imageAttachments = Array.isArray(skuCard?.imageAttachments)
      ? skuCard.imageAttachments
      : [];
    const sourceIndex = imageAttachments.findIndex(
      (image) => image.attachmentId === sourceImage.attachmentId,
    );
    const targetIndex = imageAttachments.findIndex(
      (image) => image.attachmentId === targetAttachmentId,
    );

    if (sourceIndex === -1 || targetIndex === -1) {
      return;
    }

    const reorderedImages = [...imageAttachments];
    const [movedImage] = reorderedImages.splice(sourceIndex, 1);
    reorderedImages.splice(targetIndex, 0, movedImage);

    const nextImageAttachments = reorderedImages.map((image, index) => ({
      ...image,
      attachmentOrder: index + 1,
      attachment: {
        ...image.attachment,
        attachmentOrder: index + 1,
      },
    }));
    applySkuImageOrder(skuIndex, nextImageAttachments);
    setDirtySkuImageOrderIndexes((prev) => ({
      ...prev,
      [skuIndex]: true,
    }));
  };

  const submitSkuImageOrder = async (skuIndex) => {
    const skuCard = currentSkuCards[skuIndex];
    const skuId = cleanDisplayValue(skuCard?.skuId);
    const imageAttachments = Array.isArray(skuCard?.imageAttachments)
      ? skuCard.imageAttachments
      : [];
    const request = imageAttachments.map((image, index) => ({
      attachmentId: image.attachmentId,
      attachmentOrder: index + 1,
      productId,
      skuId,
    }));

    if (
      !productId ||
      !skuId ||
      !request.length ||
      request.some((item) => !item.attachmentId)
    ) {
      toast.error("Image order could not be updated.");
      return;
    }

    setImageOrderSavingSkuIndex(skuIndex);

    try {
      const response = await ProductController.updateProductAttachmentOrder({
        Authorization: userService.getUser(),
        request,
      });

      assertControllerSuccess(response, "Image order could not be updated.");
      setDirtySkuImageOrderIndexes((prev) => {
        const next = { ...prev };
        delete next[skuIndex];
        return next;
      });
      toast.success("Image order updated.");
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Image order could not be updated."),
      );
    } finally {
      setImageOrderSavingSkuIndex(null);
    }
  };
  const SetProductAttachmentPrimary = async (skuCard, image) => {
    const attachmentId = image?.attachmentId;
    const skuId = cleanDisplayValue(skuCard?.skuId);

    if (!productId || !skuId || !attachmentId) {
      toast.error("Primary image could not be updated.");
      return;
    }

    const request = {
      attachmentId,
      isPrimary: true,
      productId,
      skuId,
    };

    try {
      setPrimaryAttachmentSavingId(attachmentId);
      const setPrimaryImage =
        await ProductController.updateProductAttachmentPrimary({
          Authorization: userService.getUser(),
          request,
        });
      assertControllerSuccess(
        setPrimaryImage,
        "Primary image could not be updated.",
      );
      toast.success("Primary image updated.");
      window.location.reload();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Primary image could not be updated."),
      );
    } finally {
      setPrimaryAttachmentSavingId(null);
    }
  };
  useEffect(() => {
    if (openColorPickerSkuId === null) return;

    const onOutsideClick = (event) => {
      const activeDropdownRef = colorDropdownRefs.current[openColorPickerSkuId];
      if (activeDropdownRef && !activeDropdownRef.contains(event.target)) {
        setOpenColorPickerSkuId(null);
      }
    };

    const onEsc = (event) => {
      if (event.key === "Escape") {
        setOpenColorPickerSkuId(null);
      }
    };

    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openColorPickerSkuId]);
  const AddProductImage = async (targetProductId, files) => {
    const selectedFiles = Array.from(files || []);
    const uploadTarget = imageUploadTargetRef.current || activeProduct;
    const resolvedProductId =
      targetProductId ||
      uploadTarget?.productId ||
      uploadTarget?.id ||
      productId;
    if (!resolvedProductId || !selectedFiles.length) return;

    try {
      setImageAdding(true);
      // setActionError("");

      const catalogResponse = await ProductController.getProductCatalogDetails({
        Authorization: userService.getUser(),
        productRequest: {
          businessProfileID: userService.getBPID(),
          companyId: userService.getCompID(),
          productId: resolvedProductId,
        },
      });
      assertControllerSuccess(
        catalogResponse,
        "Failed to load product catalog details.",
      );

      const catalogDetails =
        catalogResponse?.data ?? catalogResponse?.response?.data;
      const catalogSkus = Array.isArray(catalogDetails?.skus)
        ? catalogDetails.skus
        : [];
      if (!catalogSkus.length) {
        throw new Error(
          "This product does not have a SKU to attach images to.",
        );
      }

      const loadedDetails = uploadTarget?.details || rawProductDetails || {};
      const loadedSkus = Array.isArray(loadedDetails?.skus)
        ? loadedDetails.skus
        : [];
      const findLoadedSku = (catalogSku) =>
        loadedSkus.find(
          (loadedSku) =>
            String(loadedSku?.skuID || loadedSku?.skuId || "") ===
            String(catalogSku?.skuID || catalogSku?.skuId || ""),
        ) || {};
      const resolvedCountryOfProduction =
        catalogDetails?.basicInfo?.countryOfProduction ??
        loadedDetails?.basicInfo?.countryOfProduction ??
        parsedJwt?.countryName ??
        parsedJwt?.country ??
        "LEBANON";
      const resolveDimensions = (...dimensionsList) => {
        const dimensions = dimensionsList.find(
          (item) => item && typeof item === "object",
        );

        return {
          height: dimensions?.height,
          length: dimensions?.length,
          unitOfDimensions: dimensions?.unitOfDimensions,
          width: dimensions?.width,
        };
      };

      const processedImages = await Promise.all(
        selectedFiles.map((file) => Utils.processImageFile(file)),
      );
      const newImageAttachments = processedImages.map((image, index) => {
        const file = selectedFiles[index];
        return {
          attachmentStatus: "DEFAULT",
          attachmentType: "PRODUCT_IMAGE",
          countryName: resolvedCountryOfProduction ?? "Lebanon",
          customPath: "",
          data: Utils.padBase64(image.base64),
          extension: String(file?.name || "")
            .split(".")
            .pop()
            .toLowerCase(),
          objectName: file?.name || image.name,
          userName:
            Utils.parseJwt(userService.getUser()).username ||
            parsedJwt?.username ||
            parsedJwt?.sub ||
            parsedJwt?.name,
        };
      });

      const targetSkuId =
        uploadTarget?.skuId ||
        uploadTarget?.details?.skus?.[0]?.skuID ||
        uploadTarget?.details?.skus?.[0]?.skuId;
      const foundTargetSkuIndex = catalogSkus.findIndex(
        (sku) =>
          String(sku?.skuID || sku?.skuId || "") === String(targetSkuId || ""),
      );
      const fallbackSkuIndex =
        !targetSkuId &&
        Number.isInteger(uploadTarget?.skuIndex) &&
        uploadTarget.skuIndex >= 0 &&
        uploadTarget.skuIndex < catalogSkus.length
          ? uploadTarget.skuIndex
          : -1;
      const targetSkuIndex =
        foundTargetSkuIndex >= 0 ? foundTargetSkuIndex : fallbackSkuIndex;

      if (targetSkuIndex < 0) {
        throw new Error("No matching SKU was found for these images.");
      }

      const productRequestDto = {
        basicInfo: {
          brand: catalogDetails?.basicInfo?.brand,
          category: catalogDetails?.basicInfo?.category,
          categoryId:
            catalogDetails?.basicInfo?.categoryId ??
            loadedDetails?.basicInfo?.categoryId,
          categoryName:
            catalogDetails?.basicInfo?.categoryName ??
            loadedDetails?.basicInfo?.categoryName,
          countryOfProduction: resolvedCountryOfProduction,
          deliveryMethod:
            catalogDetails?.basicInfo?.deliveryMethod ??
            loadedDetails?.basicInfo?.deliveryMethod,
          description: catalogDetails?.basicInfo?.description,
          productName: catalogDetails?.basicInfo?.productName,
          subCategoryId:
            catalogDetails?.basicInfo?.subCategoryId ??
            loadedDetails?.basicInfo?.subCategoryId,
          subCategoryName:
            catalogDetails?.basicInfo?.subCategoryName ??
            loadedDetails?.basicInfo?.subCategoryName,
        },
        companyId: catalogDetails?.companyId || userService.getCompID(),
        skus: catalogSkus.map((sku, skuIndex) => {
          const loadedSku = findLoadedSku(sku);
          const existingImages = Array.isArray(sku?.productImages)
            ? sku.productImages
            : Array.isArray(loadedSku?.productImages)
              ? loadedSku.productImages
              : [];
          const existingVideos = Array.isArray(sku?.productVideos)
            ? sku.productVideos
            : Array.isArray(loadedSku?.productVideos)
              ? loadedSku.productVideos
              : [];

          return {
            azaleaBarcode: sku?.azaleaBarcode ?? loadedSku?.azaleaBarcode,
            barcode: sku?.barcode ?? loadedSku?.barcode,
            colorInfo: Array.isArray(sku?.colorInfo)
              ? sku.colorInfo
              : Array.isArray(loadedSku?.colorInfo)
                ? loadedSku.colorInfo
                : [],
            costPriceValue:
              sku?.costPriceValue ??
              loadedSku?.costPriceValue ??
              catalogDetails?.pricingAndProfit?.costPrice,
            currency: sku?.currency ?? loadedSku?.currency,
            dimensions: resolveDimensions(
              sku?.dimensions,
              loadedSku?.dimensions,
              sku?.productDimensions,
              loadedSku?.productDimensions,
              sku?.packageDimensions,
              loadedSku?.packageDimensions,
            ),
            initialStockQuantity:
              sku?.initialStockQuantity ?? loadedSku?.initialStockQuantity,
            material: sku?.material ?? loadedSku?.material,
            model: sku?.model ?? loadedSku?.model,
            packageDimensions: resolveDimensions(
              sku?.packageDimensions,
              loadedSku?.packageDimensions,
            ),
            packageWeight: sku?.packageWeight ?? loadedSku?.packageWeight,
            packageWeightUnitOfMeasure:
              sku?.packageWeightUnitOfMeasure ??
              loadedSku?.packageWeightUnitOfMeasure,
            price:
              sku?.price ??
              loadedSku?.price ??
              catalogDetails?.pricingAndProfit?.defaultBasePrice,
            productDimensions: resolveDimensions(
              sku?.productDimensions,
              loadedSku?.productDimensions,
            ),
            productImages:
              skuIndex === targetSkuIndex
                ? [...existingImages, ...newImageAttachments]
                : existingImages,
            productVideos: existingVideos,
            productWeight: sku?.productWeight ?? loadedSku?.productWeight,
            productWeightUnitOfMeasure:
              sku?.productWeightUnitOfMeasure ??
              loadedSku?.productWeightUnitOfMeasure,
            reservedStockQuantity:
              sku?.reservedStockQuantity ?? loadedSku?.reservedStockQuantity,
            size: sku?.size ?? loadedSku?.size,
            skuCode: sku?.skuCode ?? loadedSku?.skuCode,
            skuID:
              sku?.skuID || sku?.skuId || loadedSku?.skuID || loadedSku?.skuId,
            stockQuantity: sku?.stockQuantity ?? loadedSku?.stockQuantity,
            technicalSpecs: sku?.technicalSpecs ?? loadedSku?.technicalSpecs,
            unitOfMeasure: sku?.unitOfMeasure ?? loadedSku?.unitOfMeasure,
          };
        }),
        userId: Utils.getUserIdFromJwt(jwt) || "",
        videoUrls: Array.isArray(catalogDetails?.videoUrls)
          ? catalogDetails.videoUrls
          : [],
      };

      const updateResponse = await ProductController.updateProduct({
        Authorization: userService.getUser(),
        productId: resolvedProductId,
        productRequestDto,
      });
      assertControllerSuccess(updateResponse, "Failed to add product images.");
      const refreshed = await refreshProductCatalogDetails();
      const refreshedSku = Array.isArray(refreshed?.skus)
        ? refreshed.skus[targetSkuIndex]
        : null;
      const refreshedTarget = {
        ...(uploadTarget || {}),
        productId: resolvedProductId,
        id: resolvedProductId,
        skuId:
          refreshedSku?.skuID ||
          refreshedSku?.skuId ||
          uploadTarget?.skuId ||
          targetSkuId,
        skuIndex: targetSkuIndex,
        details: refreshed,
      };
      imageUploadTargetRef.current = refreshedTarget;
      setActiveProduct(refreshedTarget);
      // setActiveImages(getSkuProductImages(refreshedSku));
      toast.success("Product images added.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to add product images."));
    } finally {
      setImageAdding(false);
    }
  };
  const handleProductImageSelection = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    const uploadTarget = imageUploadTargetRef.current || activeProduct;
    await AddProductImage(uploadTarget?.productId || uploadTarget?.id, files);
  };
  const handleBarcodeDetected = (barcodeValue) => {
    if (!barcodeScannerTarget) return;

    setDraftSkuValue(
      barcodeScannerTarget.skuIndex,
      barcodeScannerTarget.fieldKey,
      barcodeValue,
    );
    setBarcodeScannerTarget(null);
  };

  const setDraftAttributeValue = (attributeIndex, value) => {
    setDraftProductDetails((prev) => ({
      ...prev,
      technicalAttributes: prev.technicalAttributes.map((attribute, index) =>
        index === attributeIndex ? { ...attribute, value } : attribute,
      ),
    }));
  };

  const currentProductDetails = isEditing
    ? draftProductDetails
    : productDetails;
  const currentSkuCards = isEditing ? draftSkuCards : skuCards;

  return (
    <div className="productCatalogDetails-main-container">
      <input
        ref={productImageInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={handleProductImageSelection}
      />

      {catalogDetailsLoading ? (
        <div className="productCatalogDetails-field-value">
          Loading product details...
        </div>
      ) : null}

      {!catalogDetailsLoading && catalogDetailsError ? (
        <div className="productCatalogDetails-field-value">
          {catalogDetailsError}
        </div>
      ) : null}

      {!catalogDetailsLoading && catalogSaveMessage ? (
        <div className="productCatalogDetails-field-value">
          {catalogSaveMessage}
        </div>
      ) : null}

      <div className="productCatalogDetails-summary-shell">
        <div className="productCatalogDetails-summary-left">
          <div className="productCatalogDetails-summary-item">
            <div className="productCatalogDetails-summary-label">
              Product Name
            </div>
            <div className="productCatalogDetails-summary-value">
              {currentProductDetails.summary.productName}
            </div>
          </div>
          <div className="productCatalogDetails-summary-divider" />
          <div className="productCatalogDetails-summary-item">
            <div className="productCatalogDetails-summary-label">Category</div>
            <div className="productCatalogDetails-summary-value">
              {currentProductDetails.summary.category}
            </div>
          </div>
          <div className="productCatalogDetails-summary-divider" />
          <div className="productCatalogDetails-summary-item">
            <div className="productCatalogDetails-summary-label">Status</div>
            <div className="productCatalogDetails-summary-badge">
              {currentProductDetails.summary.status}
            </div>
          </div>
        </div>

        {isEditing ? (
          <div className="productCatalogDetails-summary-edit-actions">
            <GloablButton
              width="88px"
              height="34px"
              backgroundColor="#FFFFFF"
              text="Cancel"
              color="#424242"
              fontWeight={600}
              fontSize="12px"
              lineHeight="16px"
              border="1px solid #D0D5DD"
              borderRadius="999px"
              padding="8px 12px"
              onClick={cancelEditing}
            />
            <GloablButton
              width="88px"
              height="34px"
              backgroundColor="#4072C8"
              text={catalogSaveLoading ? "Saving..." : "Save"}
              color="#FFFFFF"
              fontWeight={600}
              fontSize="12px"
              lineHeight="16px"
              borderRadius="999px"
              padding="8px 12px"
              boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.45)"
              onClick={saveEditing}
            />
          </div>
        ) : (
          <button
            type="button"
            className="productCatalogDetails-summary-edit-action"
            onClick={startEditing}
          >
            <div className="productCatalogDetails-summary-edit-icon">
              <PensilIcon />
            </div>
            <div className="productCatalogDetails-summary-edit-text">Edit</div>
          </button>
        )}
      </div>

      <div className="productCatalogDetails-content-grid">
        <div className="productCatalogDetails-left-column">
          <div className="productCatalogDetails-section-title">
            Basic Product Information
          </div>
          <div className="productCatalogDetails-card productCatalogDetails-card-basic">
            <div className="productCatalogDetails-basic-grid">
              <DetailField
                label="Product Name"
                value={currentProductDetails.basicInfo.productName}
                isEditing={isEditing}
                error={fieldErrors["basicInfo.productName"]}
                onChange={(value) =>
                  setDraftSectionValue("basicInfo", "productName", value)
                }
              />
              <DetailField
                label="Category"
                value={currentProductDetails.basicInfo.category}
                isEditing={isEditing}
                error={fieldErrors["basicInfo.category"]}
                onChange={handleCategoryChange}
              />
              <DetailField
                label="Sub Category"
                value={currentProductDetails.basicInfo.subCategoryName}
                isEditing={isEditing}
                onChange={selectSubCategory}
                subCategories={subCategories}
                selectedSubCategoryId={
                  selectedSubCategoryId ||
                  cleanDisplayValue(
                    currentProductDetails.basicInfo.subCategoryId,
                  )
                }
                onSubCategoryChange={selectSubCategory}
                error={fieldErrors["basicInfo.subCategory"]}
              />
              <DetailField
                label="Brand"
                value={currentProductDetails.basicInfo.brand}
                isEditing={isEditing}
                onChange={(value) =>
                  setDraftSectionValue("basicInfo", "brand", value)
                }
              />
              <DetailField
                label="Country of Production"
                value={currentProductDetails.basicInfo.countryOfProduction}
                isEditing={isEditing}
                onChange={(value) =>
                  setDraftSectionValue(
                    "basicInfo",
                    "countryOfProduction",
                    value,
                  )
                }
              />

              <DetailField
                label="Delivery Method"
                value={currentProductDetails.basicInfo.deliveryMethod}
                isEditing={isEditing}
                onChange={(value) =>
                  setDraftSectionValue("basicInfo", "deliveryMethod", value)
                }
              />
            </div>

            <div className="productCatalogDetails-description-block">
              <DetailField
                label="Description"
                value={currentProductDetails.basicInfo.description}
                isEditing={isEditing}
                onChange={(value) =>
                  setDraftSectionValue("basicInfo", "description", value)
                }
                maxLength={DESCRIPTION_MAX_LENGTH}
                multiline
              />
            </div>
          </div>

          <div className="productCatalogDetails-section-title">
            Commercial Data (SKU List)
          </div>

          {currentSkuCards.length ? (
            currentSkuCards.map((skuCard, skuIndex) => (
              <div
                key={`${skuCard.sku}-${skuIndex}`}
                className="productCatalogDetails-card"
              >
                <div className="productCatalogDetails-sku-header">
                  <div className="productCatalogDetails-sku-title">
                    {skuCard.sku}
                  </div>
                  <div className="productCatalogDetails-sku-stock">
                    {skuCard.stock}
                  </div>
                </div>

                <div className="productCatalogDetails-sku-images">
                  <div className="productCatalogDetails-sku-images-header">
                    <div className="productCatalogDetails-field-label">
                      Product Images
                      {imageOrderSavingSkuIndex === skuIndex
                        ? " - Updating order..."
                        : ""}
                    </div>
                    <div className="productCatalogDetails-sku-images-actions">
                      <button
                        type="button"
                        className="ai-enhance-add-btn"
                        onClick={() => openImageUploadForSku(skuCard, skuIndex)}
                        disabled={imageAdding}
                      >
                        {imageAdding && activeProduct?.skuIndex === skuIndex
                          ? "Adding..."
                          : "+ Add"}
                      </button>
                      {/* <button
                        type="button"
                        className="productCatalogDetails-sku-ai-button"
                        onClick={() => openAiEnhanceForSku(skuCard, skuIndex)}
                      >
                        AI Enhance
                      </button> */}
                      {skuCard.imageAttachments?.length ? (
                        <button
                          type="button"
                          className="productCatalogDetails-sku-image-submit"
                          disabled={
                            !dirtySkuImageOrderIndexes[skuIndex] ||
                            imageOrderSavingSkuIndex !== null
                          }
                          onClick={() => submitSkuImageOrder(skuIndex)}
                        >
                          Submit Image Order
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {skuCard.imageAttachments?.length ? (
                    <div className="productCatalogDetails-sku-image-grid">
                      {skuCard.imageAttachments.map((image, imageIndex) => (
                        <div
                          key={`${skuCard.skuId || skuCard.skuCode}-${image.attachmentId || imageIndex}`}
                          className="productCatalogDetails-sku-image-frame"
                        >
                          <img
                            src={image.src}
                            alt={`${skuCard.sku || "SKU"} ${imageIndex + 1}`}
                            className={`productCatalogDetails-sku-image${
                              image.isPrimary ? " is-primary" : ""
                            }${
                              draggedSkuImage?.skuIndex === skuIndex &&
                              draggedSkuImage?.attachmentId ===
                                image.attachmentId
                                ? " is-dragging"
                                : ""
                            }${
                              dragOverSkuImage?.skuIndex === skuIndex &&
                              dragOverSkuImage?.attachmentId ===
                                image.attachmentId
                                ? " is-drag-over"
                                : ""
                            }`}
                            draggable={
                              Boolean(image.attachmentId) &&
                              imageOrderSavingSkuIndex === null
                            }
                            onDragStart={(event) =>
                              handleSkuImageDragStart(
                                event,
                                skuIndex,
                                image.attachmentId,
                              )
                            }
                            onDragOver={(event) =>
                              handleSkuImageDragOver(
                                event,
                                skuIndex,
                                image.attachmentId,
                              )
                            }
                            onDrop={(event) =>
                              handleSkuImageDrop(
                                event,
                                skuIndex,
                                image.attachmentId,
                              )
                            }
                            onDragEnd={clearSkuImageDragState}
                          />
                          <button
                            type="button"
                            className={`productCatalogDetails-sku-primary-button${
                              image.isPrimary ? " is-primary" : ""
                            }`}
                            title={
                              image.isPrimary
                                ? "Primary image"
                                : "Set as primary image"
                            }
                            aria-label={
                              image.isPrimary
                                ? "Primary image"
                                : "Set as primary image"
                            }
                            disabled={
                              !image.attachmentId ||
                              primaryAttachmentSavingId === image.attachmentId
                            }
                            onClick={() =>
                              SetProductAttachmentPrimary(skuCard, image)
                            }
                          >
                            <PrimaryStar />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="productCatalogDetails-sku-image-empty">
                      No images found.
                    </div>
                  )}
                </div>

                <div className="productCatalogDetails-sku-grid">
                  <DetailField
                    label="Size"
                    value={skuCard.size}
                    isEditing={false}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "size", value)
                    }
                  />
                  <DetailField
                    label="Model"
                    value={skuCard.model}
                    isEditing={isEditing}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "model", value)
                    }
                  />
                  <DetailField
                    label="Material"
                    value={skuCard.material}
                    isEditing={isEditing}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "material", value)
                    }
                  />
                  <DetailField
                    label="Color"
                    value={skuCard.color}
                    isEditing={isEditing}
                    error={fieldErrors[`sku.${skuIndex}.color`]}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "color", value)
                    }
                    colorValue={skuCard.colorValue || ""}
                    colorPickerRef={(element) => {
                      colorDropdownRefs.current[
                        getSkuColorPickerId(skuCard, skuIndex)
                      ] = element;
                    }}
                    isColorPickerOpen={
                      openColorPickerSkuId ===
                      getSkuColorPickerId(skuCard, skuIndex)
                    }
                    colorSearchValue={
                      colorPresetSearchBySkuId[
                        getSkuColorPickerId(skuCard, skuIndex)
                      ] || ""
                    }
                    filteredColorPresets={getFilteredColorPresets(
                      colorPresetSearchBySkuId[
                        getSkuColorPickerId(skuCard, skuIndex)
                      ],
                    )}
                    onToggleColorPicker={() => {
                      const colorPickerId = getSkuColorPickerId(
                        skuCard,
                        skuIndex,
                      );
                      setOpenColorPickerSkuId((prev) =>
                        prev === colorPickerId ? null : colorPickerId,
                      );
                    }}
                    onColorSearchChange={(searchValue) =>
                      updateColorPresetSearch(
                        getSkuColorPickerId(skuCard, skuIndex),
                        searchValue,
                      )
                    }
                    onColorSelect={(colorNameValue, colorHexValue) => {
                      setDraftSkuColor(skuIndex, colorNameValue, colorHexValue);
                      setOpenColorPickerSkuId(null);
                    }}
                    onColorHexChange={(colorHexValue) =>
                      handleColorHexChange(skuIndex, colorHexValue)
                    }
                    onColorHexBlur={() => handleColorHexBlur(skuIndex)}
                  />
                  <DetailField
                    label="Stock Quantity"
                    value={skuCard.quantity}
                    isEditing={isEditing}
                    fieldType="integer"
                    inputMode="numeric"
                    error={fieldErrors[`sku.${skuIndex}.quantity`]}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "quantity", value)
                    }
                  />
                  {/* <DetailField
                    label="Unit of Measure"
                    value={skuCard.unit}
                    isEditing={isEditing}
                    fieldType="unitOfMeasure"
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "unit", value)
                    }
                  /> */}
                  <DetailField
                    label="Barcode"
                    value={skuCard.barcode}
                    isEditing={isEditing}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "barcode", value)
                    }
                    fieldType="barcode"
                    error={fieldErrors[`sku.${skuIndex}.barcode`]}
                    inputMode="text"
                    actionIcon={<ScannerIcon />}
                    actionLabel="Open barcode scanner"
                    onActionClick={() =>
                      setBarcodeScannerTarget({
                        skuIndex,
                        fieldKey: "barcode",
                      })
                    }
                    secondaryActionIcon={<RefreshIcon />}
                    secondaryActionLabel="Generate barcode"
                    onSecondaryActionClick={() =>
                      handleGenerateBarcode(skuIndex)
                    }
                  />
                  <DetailField
                    label="Azalea Barcode"
                    value={skuCard.azaleaBarcode}
                    isEditing={isEditing}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "azaleaBarcode", value)
                    }
                    fieldType="azaleaBarcode"
                    error={fieldErrors[`sku.${skuIndex}.azaleaBarcode`]}
                    inputMode="text"
                    actionIcon={<ScannerIcon />}
                    actionLabel="Open Azalea barcode scanner"
                    onActionClick={() =>
                      setBarcodeScannerTarget({
                        skuIndex,
                        fieldKey: "azaleaBarcode",
                      })
                    }
                    secondaryActionIcon={<RefreshIcon />}
                    secondaryActionLabel="Generate Azalea barcode"
                    onSecondaryActionClick={() =>
                      handleGenerateAzaleaBarcode(skuIndex)
                    }
                  />
                  <DetailField
                    label="Product Dimensions"
                    value={skuCard.productDimensions}
                    isEditing={isEditing}
                    fieldType="dimension"
                    error={fieldErrors[`sku.${skuIndex}.productDimensions`]}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "productDimensions", value)
                    }
                  />
                  <DetailField
                    label="Package Dimensions"
                    value={skuCard.packageDimensions}
                    isEditing={isEditing}
                    fieldType="dimension"
                    error={fieldErrors[`sku.${skuIndex}.packageDimensions`]}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "packageDimensions", value)
                    }
                  />
                  <DetailField
                    label="Product Weight"
                    value={skuCard.productWeight}
                    isEditing={isEditing}
                    fieldType="weight"
                    error={fieldErrors[`sku.${skuIndex}.productWeight`]}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "productWeight", value)
                    }
                  />

                  <DetailField
                    label="Package Weight"
                    value={skuCard.packageWeight}
                    isEditing={isEditing}
                    fieldType="weight"
                    error={fieldErrors[`sku.${skuIndex}.packageWeight`]}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "packageWeight", value)
                    }
                  />
                  <DetailField
                    label="Cost Price"
                    value={skuCard.costPriceValue}
                    isEditing={isEditing}
                    fieldType="decimal"
                    inputMode="decimal"
                    error={fieldErrors[`sku.${skuIndex}.costPriceValue`]}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "costPriceValue", value)
                    }
                  />
                  <DetailField
                    label="Selling Price"
                    value={skuCard.price}
                    isEditing={isEditing}
                    fieldType="decimal"
                    inputMode="decimal"
                    error={fieldErrors[`sku.${skuIndex}.price`]}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "price", value)
                    }
                  />
                  <DetailField
                    label="SKU"
                    value={skuCard.skuCode}
                    isEditing={isEditing}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "skuCode", value)
                    }
                  />
                  {/* <DetailField
                    label="SKU ID"
                    value={skuCard.skuId}
                    isEditing={isEditing}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "skuId", value)
                    }
                  /> */}
                </div>

                <div className="productCatalogDetails-description-block">
                  <DetailField
                    label="Technical Specifications"
                    value={skuCard.specs}
                    isEditing={isEditing}
                    onChange={(value) =>
                      setDraftSkuValue(skuIndex, "specs", value)
                    }
                    multiline
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="productCatalogDetails-card">
              <div className="productCatalogDetails-field-value">
                No SKUs found.
              </div>
            </div>
          )}

          <div className="productCatalogDetails-section-title">
            Technical Attributes
          </div>
          <div className="productCatalogDetails-card">
            <div className="productCatalogDetails-attribute-grid">
              {currentProductDetails.technicalAttributes.length ? (
                currentProductDetails.technicalAttributes.map(
                  (attribute, index) => (
                    <div
                      key={`${attribute.label}-${index}`}
                      className="productCatalogDetails-attribute-box"
                    >
                      <div className="productCatalogDetails-field-label">
                        {attribute.label}
                      </div>
                      {isEditing ? (
                        // <input
                        //   className="productCatalogDetails-edit-input"
                        //   value={attribute.value}
                        //   onChange={(event) =>
                        //     setDraftAttributeValue(index, event.target.value)
                        //   }
                        // />
                        <div className="productCatalogDetails-field-value">
                          {attribute.value}
                        </div>
                      ) : (
                        <div className="productCatalogDetails-field-value">
                          {attribute.value}
                        </div>
                      )}
                    </div>
                  ),
                )
              ) : (
                <div className="productCatalogDetails-field-value">
                  No technical attributes found.
                </div>
              )}
            </div>

            <div className="productCatalogDetails-description-block">
              <DetailField
                label="Description"
                value={currentProductDetails.technicalDescription}
                isEditing={false}
                onChange={(value) =>
                  setDraftProductDetails((prev) => ({
                    ...prev,
                    technicalDescription: value,
                  }))
                }
                multiline
              />
            </div>
          </div>
        </div>

        <div className="productCatalogDetails-right-column">
          <div className="productCatalogDetails-side-card productCatalogDetails-side-card-pricing">
            <div className="productCatalogDetails-side-card-title">
              Pricing & Profit
            </div>
            <div className="productCatalogDetails-side-metric productCatalogDetails-side-metric-pricing is-highlighted">
              <DetailField
                label="Default Base Price"
                value={currentProductDetails.pricing.defaultBasePrice}
                isEditing={false}
                onChange={(value) =>
                  setDraftSectionValue("pricing", "defaultBasePrice", value)
                }
              />
            </div>
            <div className="productCatalogDetails-side-metric">
              <DetailField
                label="Cost Price"
                value={currentProductDetails.pricing.costPrice}
                isEditing={false}
                fieldType="decimal"
                inputMode="decimal"
                error={fieldErrors["pricing.costPrice"]}
                onChange={(value) =>
                  setDraftSectionValue("pricing", "costPrice", value)
                }
              />
            </div>
            {/* <div className="productCatalogDetails-side-metric">
              <DetailField
                label="Profit Margin"
                value={currentProductDetails.pricing.profitMargin}
                isEditing={isEditing}
                onChange={(value) =>
                  setDraftSectionValue("pricing", "profitMargin", value)
                }
              />
            </div> */}
          </div>

          <div className="productCatalogDetails-side-card">
            <div className="productCatalogDetails-side-card-title">
              Stock Inventory
            </div>
            <div className="productCatalogDetails-side-metric">
              <DetailField
                label="Overall Availability"
                value={currentProductDetails.stock.overallAvailability}
                isEditing={false}
                onChange={(value) =>
                  setDraftSectionValue("stock", "overallAvailability", value)
                }
              />
              <DetailField
                label="Reserved"
                value={currentProductDetails.stock.reserved}
                isEditing={false}
                onChange={(value) =>
                  setDraftSectionValue("stock", "reserved", value)
                }
              />
              {/* <DetailField
                label="Damaged"
                value={currentProductDetails.stock.damaged}
                isEditing={isEditing}
                onChange={(value) =>
                  setDraftSectionValue("stock", "damaged", value)
                }
              /> */}
            </div>
          </div>

          <div className="productCatalogDetails-side-card">
            <div className="productCatalogDetails-side-card-title">
              Change History
            </div>
            <div className="productCatalogDetails-side-metric">
              <DetailField
                label="Created"
                value={currentProductDetails.history.createdDate}
                isEditing={false}
                onChange={(value) =>
                  setDraftSectionValue("history", "createdDate", value)
                }
              />
              <DetailField
                label="By"
                value={currentProductDetails.history.createdBy}
                isEditing={false}
                onChange={(value) =>
                  setDraftSectionValue("history", "createdBy", value)
                }
              />
            </div>
            <div className="productCatalogDetails-side-metric">
              <DetailField
                label="Last Modified"
                value={currentProductDetails.history.modifiedDate}
                isEditing={false}
                onChange={(value) =>
                  setDraftSectionValue("history", "modifiedDate", value)
                }
              />
              <DetailField
                label="By"
                value={currentProductDetails.history.modifiedBy}
                isEditing={false}
                onChange={(value) =>
                  setDraftSectionValue("history", "modifiedBy", value)
                }
              />
            </div>
          </div>
          {/**************************************************************************************** */}
          {/* Video Upload */}
          {/* <div className="productSubmission-brd-card-internal">
            <div className="productSubmission-brd-card-title">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 6C4 4.89543 4.89543 4 6 4H14L20 10V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z"
                  stroke="#667085"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 4V10H20"
                  stroke="#667085"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Upload File
            </div>
            Drop zone
            <div
              className={`video-upload-dropzone ${videoDragging ? "video-upload-dropzone-active" : ""}`}
              onClick={() => videoInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setVideoDragging(true);
              }}
              onDragLeave={() => setVideoDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setVideoDragging(false);
                const file = e.dataTransfer.files?.[0];
                if (file) setVideoFile(file);
              }}
            >
              <UploadButtonIcon />
              <p className="video-upload-dropzone-title">
                {videoFile ? videoFile.name : "Drag & drop your video here"}
              </p>
              {!videoFile && (
                <p className="video-upload-dropzone-sub">
                  or click to browse files
                </p>
              )}
              <p className="video-upload-dropzone-hint">
                Supported Formats: MP4, AVI, WebM
                <br />
                Maximum file size: 500MB
              </p>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/avi,video/webm"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setVideoFile(file);
                }}
              />
            </div>
            <button
              className="video-upload-btn"
              disabled={!videoFile}
              onClick={() => {
                AddVideoToProduct();
              }}
            >
              Upload Video
            </button>
            URL section
            <div className="video-url-label">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2357 14.7367 12.9315 14.9411 13.6467 14.9923C14.3618 15.0435 15.0796 14.9403 15.7513 14.6897C16.4231 14.4392 17.0331 14.047 17.54 13.54L20.54 10.54C21.4508 9.59695 21.9548 8.33394 21.9434 7.02296C21.932 5.71198 21.4061 4.45791 20.4791 3.53087C19.5521 2.60383 18.298 2.07799 16.987 2.0666C15.676 2.0552 14.413 2.55918 13.47 3.46997L11.75 5.18997"
                  stroke="#667085"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 11C13.5705 10.4259 13.0226 9.95087 12.3934 9.60711C11.7643 9.26334 11.0685 9.05888 10.3533 9.00766C9.63819 8.95645 8.92038 9.05964 8.24864 9.31018C7.5769 9.56072 6.96689 9.95296 6.46 10.46L3.46 13.46C2.54918 14.403 2.04520 15.666 2.05660 16.977C2.06800 18.288 2.59383 19.5421 3.52087 20.4691C4.44791 21.3962 5.70198 21.922 7.01296 21.9334C8.32394 21.9448 9.58695 21.4408 10.53 20.53L12.24 18.82"
                  stroke="#667085"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Video URL
            </div>
            <input
              type="url"
              className="video-url-input"
              placeholder="https://youtube.com/... or https://vimeo.com/..."
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
            />
            <p className="video-url-hint">Enter a Vimeo or Youtube link.</p>
            <button
              className="video-upload-btn"
              disabled={!videoUrl.trim()}
              onClick={() => {
                AddVideoToProduct();
              }}
            >
              Add Video
            </button>
          </div> */}
          {/**************************************************************************************** */}
        </div>
      </div>
      {barcodeScannerTarget ? (
        <SingleBarcodeScanner
          onDetected={handleBarcodeDetected}
          onClose={() => setBarcodeScannerTarget(null)}
        />
      ) : null}
    </div>
  );
}

export default ProductCatalogDetails;
